<template>
    <TooltipProvider :delayDuration="0">
        <SidebarLayout>
            <template #sidebar>
                <div class="h-[calc(100vh_-_var(--legacy-banner-height,0px)_-_128px)]">
                    <ConversationHistory
                        :conversations="conversations"
                        :current-conversation-id="currentConversationId"
                        :is-loading="isLoadingConversations"
                        :has-more="hasMoreConversations"
                        :is-loading-more="isLoadingMoreConversations"
                        @select-conversation="handleSelectConversation"
                        @new-conversation="handleNewConversation"
                        @rename-conversation="handleRenameConversation"
                        @delete-conversation="handleDeleteConversation"
                        @load-more="handleLoadMoreConversations"
                    />
                </div>
            </template>
            <template #main>
                <div ref="textareaParent" class="h-[calc(100vh_-_var(--legacy-banner-height,0px)_-_128px)]">
                        <!-- Debug Header -->
                        <!-- <div class="mb-4 flex items-center justify-end">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        @click="copyConversationToClipboard"
                                        :class="[
                                            'flex items-center rounded px-3 py-1.5 text-sm transition-all duration-300',
                                            copyButtonState === 'success'
                                                ? 'bg-green-100 text-green-700'
                                                : copyButtonState === 'error'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                                        ]"
                                        :disabled="copyButtonState === 'copying'"
                                    >
                                        <ClipboardIcon v-if="copyButtonState === 'idle'" class="mr-1 h-4 w-4" />
                                        <Loader2Icon v-else-if="copyButtonState === 'copying'" class="mr-1 h-4 w-4 animate-spin" />
                                        <CheckIcon v-else-if="copyButtonState === 'success'" class="mr-1 h-4 w-4" />
                                        <XIcon v-else-if="copyButtonState === 'error'" class="mr-1 h-4 w-4" />

                                        <span v-if="copyButtonState === 'idle'">Copy Debug</span>
                                        <span v-else-if="copyButtonState === 'copying'">Copying...</span>
                                        <span v-else-if="copyButtonState === 'success'">Copied!</span>
                                        <span v-else-if="copyButtonState === 'error'">Failed</span>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Copy conversation for debugging</TooltipContent>
                            </Tooltip>
                        </div> -->

                        <div class="flex h-full flex-1 flex-col rounded border border-gray-200 bg-white">
                            <!-- Messages -->
                            <div ref="messagesContainer" class="min-h-0 flex-1 overflow-y-auto p-6 flex flex-col" @scroll="handleScroll">
                                <!-- Welcome Screen -->
                                <div v-if="messages.length === 0" class="py-12 text-center my-auto">
                                    <div class="mb-8">
                                        <div
                                            class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100"
                                        >
                                            <BotIcon class="h-8 w-8 text-blue-500" />
                                        </div>
                                        <h2 class="mb-2 text-2xl font-bold text-gray-900">
                                            {{ firstName ? `Who are we looking for today, ${firstName}?` : 'Who are we looking for today?' }}
                                        </h2>
                                        <p class="text-sm mx-10">I'm Scout, your personal deep search assistant. Conversation isn't my strong suit (yet), but creator discovery is. Tell me who you want, and in just a few minutes I'll have a list ready for you.</p>
                                    </div>

                                    <!-- Search Input - Moved above examples -->
                                    <div class="mb-8">
                                        <form class="mx-auto max-w-2xl" @submit.prevent="handleSubmit">
                                                <!-- Input container with + button and send button inside -->
                                                <div class="flex flex-1 rounded-lg border border-gray-300 bg-white focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500">
                                                    <!-- Add List Button -->
                                                    <div class="flex-shrink-0 self-end pb-1.5 pl-1.5">
                                                        <DropdownMenu v-model:open="isWelcomeListDropdownOpen">
                                                            <DropdownMenuTrigger class="flex h-9 w-9 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-accent hover:text-gray-600 focus:outline-none">
                                                                <PlusIcon class="h-5 w-5" />
                                                            </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="start" class="w-[220px]">
                                                            <DropdownMenuLabel>Attach a List</DropdownMenuLabel>
                                                            <DropdownMenuSeparator />
                                                            <div v-if="isLoadingLists" class="px-2 py-3 text-center text-sm text-gray-500">
                                                                <Loader2Icon class="mx-auto h-4 w-4 animate-spin" />
                                                            </div>
                                                            <div v-else-if="!userLists || userLists.length === 0" class="px-2 py-2">
                                                                <p class="text-sm text-gray-500 mb-2">You have no lists yet.</p>
                                                                <router-link
                                                                    :to="{ name: 'lists' }"
                                                                    class="text-sm text-blue-500 hover:text-blue-700 font-medium"
                                                                >
                                                                    Click here to create one
                                                                </router-link>
                                                            </div>
                                                            <template v-else>
                                                                <div v-if="availableLists.length > 0" class="sticky top-0 bg-white px-2 py-1.5">
                                                                    <input
                                                                        v-model="listSearchText"
                                                                        type="text"
                                                                        placeholder="Search lists…"
                                                                        class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                                                        @click.stop
                                                                        @keydown.stop
                                                                    />
                                                                </div>
                                                                <div v-if="availableLists.length === 0" class="px-2 py-2">
                                                                    <p class="text-sm text-gray-500">All lists attached.</p>
                                                                </div>
                                                                <div v-else-if="filteredAvailableLists.length === 0" class="px-2 py-2">
                                                                    <p class="text-sm text-gray-500">No matching lists.</p>
                                                                </div>
                                                                <div v-else class="max-h-[280px] overflow-y-auto">
                                                                    <DropdownMenuItem
                                                                        v-for="list in filteredAvailableLists"
                                                                        :key="list.id"
                                                                        class="cursor-pointer"
                                                                        @click="handleListSelect(list)"
                                                                    >
                                                                        <span class="truncate">{{ list.name }}</span>
                                                                    </DropdownMenuItem>
                                                                </div>
                                                            </template>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                    <div class="flex-1 min-w-0 flex flex-col">
                                                        <div v-if="selectedLists.length > 0" class="flex flex-wrap gap-1.5 pt-2 pr-2">
                                                            <Badge
                                                                v-for="list in selectedLists"
                                                                :key="list.id"
                                                                variant="secondary"
                                                                class="gap-1 pl-1.5"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    class="flex items-center justify-center h-3.5 w-3.5 rounded-full hover:bg-gray-300 transition-colors"
                                                                    @click="handleListRemove(list.id)"
                                                                >
                                                                    <XIcon class="h-2.5 w-2.5" />
                                                                </button>
                                                                {{ list.name }}
                                                            </Badge>
                                                        </div>
                                                        <textarea
                                                            ref="autoGrowWelcome"
                                                            v-model="input"
                                                            v-auto-grow
                                                            rows="1"
                                                            placeholder="Enter a deep search prompt to begin"
                                                            :disabled="isLoading"
                                                            class="w-full resize-none overflow-hidden border-0 bg-transparent py-3 pr-4 text-slate-900 focus:outline-none focus:ring-0"
                                                            @keydown.enter.exact.prevent="handleSubmit"
                                                        />
                                                    </div>
                                                    <!-- Send Button inside box -->
                                                    <div class="flex-shrink-0 self-end pb-1.5 pr-1.5">
                                                        <Button
                                                            type="submit"
                                                            size="icon"
                                                            class="h-9 w-9 rounded-lg"
                                                            :disabled="isLoading || !input.trim()"
                                                        >
                                                            <SendIcon class="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                        </form>
                                    </div>

                                    <!-- Starter Prompts - Now below search input -->
                                    <div class="space-y-3 mx-auto max-w-xl">
                                        <p class="mb-4 text-sm font-medium text-gray-700">Get inspired by these starter prompts:</p>
                                        <button
                                            v-for="(query, index) in suggestedQueries.slice(0, 4)"
                                            :key="index"
                                            class="group block w-full rounded border border-gray-200 bg-gray-50 p-4 text-left transition-colors hover:bg-gray-100"
                                            @click="handleSuggestedQuery(query)"
                                        >
                                            <div class="flex items-start space-x-3">
                                                <SearchIcon
                                                    class="mt-0.5 h-4 w-4 text-gray-400 group-hover:text-blue-500"
                                                />
                                                <span class="text-sm text-gray-700 group-hover:text-gray-900">{{
                                                    query
                                                }}</span>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                <div v-else-if="isLoadingPreviousConversation">
                                    <SkeletonAIChatMessages />
                                </div>


                                <!-- Messages -->
                                <div v-else>
                                    <div
                                        v-for="message in messages.filter((m) => m && m.id)"
                                        :key="message.id"
                                        class="mb-6"
                                    >
                                        <!-- Thinking Process Box - Separate from response -->
                                        <div
                                            v-if="
                                                message.role === 'assistant' &&
                                                message.thinkingSteps &&
                                                message.thinkingSteps.length > 0
                                            "
                                        >
                                            <div
                                                class="max-w-4xl overflow-hidden rounded-lg border border-gray-300 bg-gray-50"
                                            >
                                                <!-- Thinking Header (clickable) -->
                                                <div
                                                    class="cursor-pointer border-b border-gray-300 bg-gray-100 px-4 py-3 transition-colors hover:bg-gray-200"
                                                    @click="toggleThinkingSteps(message.id)"
                                                >
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex items-center space-x-2">
                                                            <div class="h-2 w-2 rounded-full bg-blue-500"></div>
                                                            <span class="text-sm font-medium text-gray-700">
                                                                {{ message.duration ? `Thought for ${$filters.humanizeDurationShort(message.duration)}` : 'Thinking' }}
                                                            </span>
                                                        </div>
                                                        <div class="flex items-center space-x-2">
                                                            <span class="text-xs text-gray-500"
                                                                >{{ message.thinkingSteps.length }} step{{ message.thinkingSteps.length !== 1 ? 's' : '' }}</span
                                                            >
                                                            <AppIcon
                                                                name="chevron-down"
                                                                class="h-3 w-3 text-gray-500 transition-transform duration-200"
                                                                :class="{ 'rotate-180': !isThinkingExpanded(message.id) }"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Collapsed Thinking Summary (for completed messages) -->
                                                <div
                                                    v-if="!isThinkingExpanded(message.id) && message.thinkingSteps.length > 0"
                                                    class="px-4 py-3 border-b border-gray-100"
                                                >
                                                    <div class="flex items-center space-x-3">
                                                        <div class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-400">
                                                            <AppIcon name="check" class="h-2 w-2 text-white" />
                                                        </div>
                                                        <div class="flex-1 min-w-0">
                                                            <p class="text-sm text-gray-700 truncate">
                                                                {{ message.thinkingSteps[message.thinkingSteps.length - 1]?.step || 'Completed analysis' }}
                                                            </p>
                                                            <div class="text-xs text-gray-500 mt-1">
                                                                Completed {{ message.thinkingSteps.length }} step{{ message.thinkingSteps.length !== 1 ? 's' : '' }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Thinking Steps with Wizard Timeline (Expanded) -->
                                                <div v-if="isThinkingExpanded(message.id)" class="p-4">
                                                    <div class="relative">
                                                        <!-- Vertical connecting line (centered on circles) -->
                                                        <div
                                                            v-if="message.thinkingSteps.length > 1"
                                                            class="absolute left-3 w-px bg-gray-300 h-[calc(100%-2rem)]"
                                                            :style="{
                                                                top: '1.75rem',
                                                            }"
                                                        ></div>

                                                        <div class="space-y-2">
                                                            <div
                                                                v-for="(step, index) in message.thinkingSteps"
                                                                :key="index"
                                                                class="relative flex items-center space-x-2"
                                                            >
                                                                <!-- Step Circle -->
                                                                <div
                                                                    class="relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-blue-500 bg-white"
                                                                >
                                                                    <span class="text-xs font-medium text-blue-600">{{
                                                                        index + 1
                                                                    }}</span>
                                                                </div>

                                                                <!-- Step Content (no border, reduced height) -->
                                                                <div class="flex-1 py-1">
                                                                    <div class="flex items-center justify-between">
                                                                        <div class="flex-1">
                                                                            <p class="text-sm text-gray-900">
                                                                                {{ step.step }}
                                                                            </p>

                                                                            <!-- Step Details -->
                                                                            <div
                                                                                v-if="
                                                                                    step.data &&
                                                                                    (step.data.tools ||
                                                                                        step.data.query ||
                                                                                        step.data.count ||
                                                                                        step.data.type)
                                                                                "
                                                                                class="space-y-1"
                                                                            >
                                                                                <div
                                                                                    v-if="step.data.tools"
                                                                                    class="text-xs text-gray-500"
                                                                                >
                                                                                    Tools: {{ step.data.tools.join(', ') }}
                                                                                </div>
                                                                                <div
                                                                                    v-if="step.data.query"
                                                                                    class="text-xs text-gray-500"
                                                                                >
                                                                                    Query: "{{ step.data.query }}"
                                                                                </div>
                                                                                <div
                                                                                    v-if="step.data.count"
                                                                                    class="text-xs text-gray-500"
                                                                                >
                                                                                    Found: {{ step.data.count }} results
                                                                                </div>

                                                                                <!-- Deconstruction Details -->
                                                                                <div
                                                                                    v-if="
                                                                                        step.data.type ===
                                                                                            'deconstruction' &&
                                                                                        step.data.details
                                                                                    "
                                                                                    class="mt-2 text-xs"
                                                                                >
                                                                                    <div
                                                                                        class="rounded border border-blue-200 bg-blue-50 p-2"
                                                                                    >
                                                                                        <div
                                                                                            class="mb-1 font-semibold text-blue-800"
                                                                                        >
                                                                                            Query Analysis:
                                                                                        </div>
                                                                                        <div
                                                                                            v-if="
                                                                                                step.data.details.success &&
                                                                                                step.data.details
                                                                                                    .deconstruction
                                                                                            "
                                                                                            class="space-y-1 text-blue-700"
                                                                                        >
                                                                                            <div>
                                                                                                <strong>Intent:</strong>
                                                                                                {{
                                                                                                    step.data.details.deconstruction.intent_classification?.replace(
                                                                                                        '_',
                                                                                                        ' ',
                                                                                                    )
                                                                                                }}
                                                                                            </div>
                                                                                            <div>
                                                                                                <strong>Complexity:</strong>
                                                                                                {{
                                                                                                    step.data.details
                                                                                                        .deconstruction
                                                                                                        .query_complexity
                                                                                                }}
                                                                                            </div>
                                                                                            <div
                                                                                                v-if="
                                                                                                    step.data.details
                                                                                                        .deconstruction
                                                                                                        .primary_requirements
                                                                                                        ?.length
                                                                                                "
                                                                                            >
                                                                                                <strong
                                                                                                    >Requirements:</strong
                                                                                                >
                                                                                                {{
                                                                                                    step.data.details.deconstruction.primary_requirements.join(
                                                                                                        ', ',
                                                                                                    )
                                                                                                }}
                                                                                            </div>
                                                                                            <div
                                                                                                v-if="
                                                                                                    step.data.details
                                                                                                        .deconstruction
                                                                                                        .exclusions?.length
                                                                                                "
                                                                                            >
                                                                                                <strong>Exclusions:</strong>
                                                                                                {{
                                                                                                    step.data.details.deconstruction.exclusions.join(
                                                                                                        ', ',
                                                                                                    )
                                                                                                }}
                                                                                            </div>
                                                                                            <!-- Research Findings -->
                                                                                            <div
                                                                                                v-if="step.data.research_summaries?.length"
                                                                                                class="mt-3 border-t border-blue-200 pt-2"
                                                                                            >
                                                                                                <strong class="text-blue-800">🔬 Research Conducted:</strong>
                                                                                                <div v-for="(research, index) in step.data.research_summaries" :key="index" class="mt-2 rounded bg-blue-50 p-2">
                                                                                                    <div class="font-medium text-blue-900">{{ research.topic }}</div>
                                                                                                    <div v-if="research.key_findings?.length" class="mt-1 text-xs">
                                                                                                        <strong>Key Findings:</strong>
                                                                                                        <ul class="ml-3 mt-1 list-disc space-y-1">
                                                                                                            <li v-for="finding in research.key_findings" :key="finding" class="text-blue-700">{{ finding }}</li>
                                                                                                        </ul>
                                                                                                    </div>
                                                                                                    <div v-if="research.conclusion" class="mt-1 text-xs">
                                                                                                        <strong>Conclusion:</strong>
                                                                                                        <span class="text-blue-700">{{ research.conclusion }}</span>
                                                                                                    </div>
                                                                                                    <div class="mt-1 text-xs text-gray-500">
                                                                                                        Research time: {{ Math.round(research.research_time_ms / 1000) }}s
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <!-- Plan Details -->
                                                                                <div
                                                                                    v-if="
                                                                                        step.data.type === 'plan' &&
                                                                                        step.data.details
                                                                                    "
                                                                                    class="mt-2 text-xs"
                                                                                >
                                                                                    <div
                                                                                        class="rounded border border-green-200 bg-green-50 p-2"
                                                                                    >
                                                                                        <div
                                                                                            class="mb-1 font-semibold text-green-800"
                                                                                        >
                                                                                            Search Strategy:
                                                                                        </div>
                                                                                        <div
                                                                                            v-if="
                                                                                                step.data.details.success &&
                                                                                                step.data.details.plan
                                                                                            "
                                                                                            class="space-y-1 text-green-700"
                                                                                        >
                                                                                            <div>
                                                                                                <strong>Approach:</strong>
                                                                                                {{
                                                                                                    step.data.details.plan.search_strategy?.replace(
                                                                                                        '_',
                                                                                                        ' ',
                                                                                                    )
                                                                                                }}
                                                                                            </div>
                                                                                            <div
                                                                                                v-if="
                                                                                                    step.data.details.plan
                                                                                                        .execution_steps
                                                                                                        ?.length
                                                                                                "
                                                                                            >
                                                                                                <strong>Steps:</strong>
                                                                                                {{
                                                                                                    step.data.details.plan
                                                                                                        .execution_steps
                                                                                                        .length
                                                                                                }}
                                                                                                planned
                                                                                            </div>
                                                                                            <div
                                                                                                v-if="
                                                                                                    step.data.details.plan
                                                                                                        .relevance_criteria
                                                                                                        ?.length
                                                                                                "
                                                                                            >
                                                                                                <strong
                                                                                                    >Success
                                                                                                    Criteria:</strong
                                                                                                >
                                                                                                {{
                                                                                                    step.data.details.plan.relevance_criteria
                                                                                                        .slice(0, 2)
                                                                                                        .join(', ')
                                                                                                }}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <!-- Timing Badge -->
                                                                        <div class="ml-3 flex-shrink-0">
                                                                            <span class="text-xs text-gray-500">
                                                                                {{
                                                                                    formatStepTiming(
                                                                                        step,
                                                                                        index,
                                                                                        message.thinkingSteps,
                                                                                    )
                                                                                }}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Action Result Indicator -->
                                        <div v-if="message.role === 'action_result'" class="flex items-center gap-1.5 py-1 text-sm text-gray-500">
                                            <CheckIcon v-if="!message.cancelled" class="h-3.5 w-3.5 text-green-500" />
                                            <XIcon v-else class="h-3.5 w-3.5 text-gray-400" />
                                            {{ message.content }}
                                        </div>

                                        <!-- AI Response Box - Separate from thinking -->
                                        <div v-if="message.role !== 'action_result'" :class="['flex', message.role === 'user' ? 'justify-end' : 'justify-start']">
                                            <div
                                                v-if="message.role === 'user'"
                                                class="group/message relative w-full"
                                                :class="editingMessageId === message.id ? 'max-w-none' : 'max-w-4xl'"
                                            >
                                                <div
                                                    class="overflow-hidden text-white"
                                                    :class="
                                                        editingMessageId === message.id
                                                            ? 'rounded-lg border border-gray-300 bg-gray-100'
                                                            : 'rounded-lg bg-blue-500'
                                                    "
                                                >
                                                    <!-- User Message Content or Edit Mode -->
                                                    <div class="p-4">
                                                        <!-- Normal display mode -->
                                                        <div v-if="editingMessageId !== message.id">
                                                            <div v-if="message.attachedLists?.length" class="mb-2 flex flex-wrap gap-1.5">
                                                                <Badge
                                                                    v-for="list in message.attachedLists"
                                                                    :key="list.id"
                                                                    variant="outline"
                                                                    class="border-blue-300/40 bg-blue-400/30 text-white"
                                                                >
                                                                    {{ list.name }}
                                                                </Badge>
                                                            </div>
                                                            <p class="whitespace-pre-wrap">{{ message.content }}</p>
                                                        </div>

                                                        <!-- Edit mode -->
                                                        <div v-else class="space-y-3">
                                                            <textarea
                                                                v-model="editingContent"
                                                                class="min-h-24 w-full resize-none rounded border border-gray-300 bg-white p-3 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Edit your message..."
                                                                @keydown.enter.ctrl="saveEditedMessage"
                                                                @keydown.escape="cancelEditingMessage"
                                                            ></textarea>
                                                            <div class="flex justify-end space-x-2">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    @click="cancelEditingMessage"
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    @click="saveEditedMessage"
                                                                >
                                                                    Save & Send
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Action buttons - always visible below bubble -->
                                                <div v-if="editingMessageId !== message.id" class="mt-1 flex justify-end opacity-0 group-hover/message:opacity-100 transition duration-250">
                                                    <div
                                                        class="flex space-x-1"
                                                    >
                                                        <!-- Copy button -->
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    class="h-8 w-8"
                                                                    @click="copyMessageToClipboard(message)"
                                                                >
                                                                    <ClipboardIcon class="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="bottom" class="bg-gray-900 text-white px-2 py-1 rounded text-xs">Copy</TooltipContent>
                                                        </Tooltip>

                                                        <!-- Retry button -->
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    class="h-8 w-8"
                                                                    @click="retryFromMessage(message)"
                                                                >
                                                                    <RefreshCwIcon class="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Try Again</TooltipContent>
                                                        </Tooltip>

                                                        <!-- Edit button -->
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    class="h-8 w-8"
                                                                    @click="startEditingMessage(message)"
                                                                >
                                                                    <PencilIcon class="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Edit Message</TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Non-user messages (assistant, error) -->
                                            <div v-else class="group relative w-full max-w-4xl">
                                                <div
                                                    v-if="message.type === 'error'"
                                                    :class="[
                                                        'overflow-hidden',
                                                        message.type === 'error'
                                                            ? 'rounded-r-lg rounded-tl-lg border border-red-200 bg-red-50'
                                                            : 'rounded-r-lg rounded-tl-lg border border-gray-200 bg-white',
                                                    ]"
                                                >
                                                    <!-- Error Message Content -->
                                                    <div class="p-4">
                                                        <div class="flex items-start space-x-3">
                                                            <div class="flex-shrink-0">
                                                                <component
                                                                    :is="getErrorIcon(message.errorType)"
                                                                    class="h-5 w-5 text-red-500"
                                                                />
                                                            </div>
                                                            <div class="flex-1">
                                                                <div class="flex items-center justify-between">
                                                                    <h4 class="font-medium text-red-800">
                                                                        {{ getErrorTitle(message.errorType) }}
                                                                    </h4>
                                                                    <span
                                                                        v-if="message.retryable"
                                                                        class="rounded bg-red-100 px-2 py-1 text-xs text-red-600"
                                                                    >
                                                                        Retryable
                                                                    </span>
                                                                </div>
                                                                <p class="mt-1 text-sm text-red-700">
                                                                    {{ message.content }}
                                                                </p>

                                                                <!-- Error Actions -->
                                                                <div class="mt-3 flex space-x-2">
                                                                    <Button
                                                                        v-if="message.retryable && message.retryData"
                                                                        variant="outline"
                                                                        size="xs"
                                                                        class="border-red-300 text-red-700 hover:bg-red-50"
                                                                        :disabled="retryingMessage === message.id"
                                                                        @click="retryLastRequest(message)"
                                                                    >
                                                                        <RefreshCwIcon
                                                                            v-if="!retryingMessage === message.id"
                                                                            class="mr-1 h-3 w-3"
                                                                        />
                                                                        <Loader2Icon
                                                                            v-else
                                                                            class="mr-1 h-3 w-3 animate-spin"
                                                                        />
                                                                        {{
                                                                            retryingMessage === message.id
                                                                                ? 'Retrying...'
                                                                                : 'Retry'
                                                                        }}
                                                                    </Button>

                                                                    <Button
                                                                        variant="outline"
                                                                        size="xs"
                                                                        @click="dismissError(message.id)"
                                                                    >
                                                                        <XIcon class="mr-1 h-3 w-3" />
                                                                        Dismiss
                                                                    </Button>
                                                                </div>

                                                                <!-- Rate Limit Countdown -->
                                                                <div
                                                                    v-if="
                                                                        message.errorType === 'rate_limit_error' &&
                                                                        message.retryAfter
                                                                    "
                                                                    class="mt-2"
                                                                >
                                                                    <div class="text-xs text-red-600">
                                                                        Can retry in: {{ formatRetryCountdown(message) }}
                                                                    </div>
                                                                    <div class="mt-1 h-1 w-full rounded-full bg-red-200">
                                                                        <div
                                                                            class="h-1 rounded-full bg-red-500 transition-all duration-1000"
                                                                            :style="{
                                                                                width: `${getRetryProgress(message)}%`,
                                                                            }"
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Clarification Request (for clarification messages) -->
                                                <div
                                                    v-if="message.type === 'clarification' && message.clarification_questions"
                                                    class="p-4"
                                                >
                                                    <div class="mb-4">
                                                        <div
                                                            class="prose prose-sm max-w-none"
                                                            @click="handleCreatorLinkClick($event, message)"
                                                            v-html="renderMessageContent(message.content)"
                                                        ></div>
                                                    </div>

                                                    <!-- Clarification Questions -->
                                                    <div class="space-y-3">
                                                        <div
                                                            v-for="(question, index) in message.clarification_questions"
                                                            :key="index"
                                                            class="rounded-lg border border-blue-200 bg-blue-50 p-3"
                                                        >
                                                            <div class="flex items-start space-x-2">
                                                                <div class="flex h-5 w-5 items-center justify-center rounded-full bg-blue-200 text-xs font-medium text-blue-700">
                                                                    {{ index + 1 }}
                                                                </div>
                                                                <p class="text-sm text-blue-800">{{ question }}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Default Assumption (if provided) -->
                                                    <div
                                                        v-if="message.assumptions?.assumption"
                                                        class="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
                                                    >
                                                        <p class="text-xs font-medium text-gray-600 mb-1">If no clarification is provided:</p>
                                                        <p class="text-sm text-gray-700">{{ message.assumptions.assumption }}</p>
                                                    </div>

                                                    <!-- Call to action -->
                                                    <div class="mt-4 text-sm text-gray-600">
                                                        Please provide clarification by responding with your preferences, or I'll proceed with the default assumption above.
                                                    </div>
                                                </div>

                                                <!-- Normal Text content (for assistant messages only) -->
                                                <div
                                                    v-if="message.content && message.role === 'assistant' && message.type !== 'clarification'"
                                                    class="p-4 text-gray-900"
                                                >
                                                    <div
                                                        class="prose prose-sm max-w-none"
                                                        @click="handleCreatorLinkClick($event, message)"
                                                        v-html="renderMessageContent(message.content)"
                                                    ></div>

                                                    <!-- Duration indicator for assistant messages -->
                                                    <div v-if="message.duration" class="mt-2 text-xs text-gray-500">
                                                        Completed in {{ $filters.humanizeDurationShort(message.duration) }}
                                                    </div>

                                                    <!-- Assistant action buttons: thumbs + copy -->
                                                    <div
                                                        v-if="message.role === 'assistant'"
                                                        class="mt-3 flex justify-start"
                                                    >
                                                        <div class="flex space-x-1 rounded-lg">

                                                            <!-- Copy button -->
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        class="rounded p-2 text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-500"
                                                                        @click="copyMessageToClipboard(message)"
                                                                    >
                                                                        <ClipboardIcon class="h-4 w-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Copy</TooltipContent>
                                                            </Tooltip>

                                                            <!-- Thumbs up -->
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        class="rounded p-2 transition-colors"
                                                                        :class="
                                                                            message.feedback === 'up'
                                                                                ? 'border border-green-200 bg-green-50 text-green-600'
                                                                                : 'text-gray-500 hover:bg-green-50 hover:text-green-600'
                                                                        "
                                                                        @click="giveFeedback(message, 'up')"
                                                                    >
                                                                        <ThumbsUpIcon class="h-4 w-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Good Response</TooltipContent>
                                                            </Tooltip>

                                                            <!-- Thumbs down -->
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        class="rounded p-2 transition-colors"
                                                                        :class="
                                                                            message.feedback === 'down'
                                                                                ? 'border border-red-200 bg-red-50 text-red-600'
                                                                                : 'text-gray-500 hover:bg-red-50 hover:text-red-600'
                                                                        "
                                                                        @click="giveFeedback(message, 'down')"
                                                                    >
                                                                        <ThumbsDownIcon class="h-4 w-4" />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="bottom" :sideOffset="8" :avoidCollisions="false" class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600">Bad Response</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Creator Results Component - Separate from assistant message -->
                                        <div
                                            v-if="
                                                message &&
                                                message.role === 'assistant' &&
                                                message.type !== 'error' &&
                                                ((message.creator_sample && message.creator_sample.length > 0) ||
                                                    (message.rejectedCreators && message.rejectedCreators.length > 0))
                                            "
                                            class="mt-4"
                                        >
                                            <CreatorResults
                                                :totalCreators="message.creator_count"
                                                :creators="message.creator_sample"
                                                :totalReach="message.total_followers"
                                                :rejected-creators="message.rejectedCreators"
                                                @view-full-results="openCreatorsModal(message)"
                                            />
                                        </div>

                                        <!-- Done state (history/page-refresh) - plain indicator, no box -->
                                        <div v-if="message.listAction && ['create', 'delete', 'update'].includes(message.listAction.action) && message.fromHistory" class="flex items-center gap-1.5 py-1 mt-3 text-sm text-gray-500">
                                            <CheckIcon class="h-3.5 w-3.5 text-green-500" />
                                            {{ listActionDoneText(message.listAction) }}
                                        </div>

                                        <!-- Inline List Action Confirmation -->
                                        <Alert
                                            v-if="message.listAction && ['create', 'delete', 'update'].includes(message.listAction.action) && !message.fromHistory"
                                            :variant="listActionIsDestructive(message.listAction) ? 'destructive' : 'info'"
                                            class="mt-3"
                                        >
                                            <!-- Loading state -->
                                            <div v-if="listActionState[message.id] === 'loading'" class="flex items-center gap-2 text-sm font-medium" :class="listActionIsDestructive(message.listAction) ? 'text-red-700' : 'text-blue-700'">
                                                <Loader2Icon class="h-4 w-4 animate-spin" />
                                                {{ listActionLoadingText(message.listAction) }}
                                            </div>
                                            <!-- Default: prompt with buttons -->
                                            <template v-else>
                                                <!-- Create prompt -->
                                                <template v-if="message.listAction.action === 'create'">
                                                    <p class="text-sm text-gray-700">
                                                        Would you like to create a list
                                                        <span v-if="listActionEditing !== message.id" class="inline-flex items-center gap-1">
                                                            "<strong>{{ message.listAction.suggested_name }}</strong>"
                                                            <button class="inline-flex text-gray-400 hover:text-blue-600" title="Edit name" @click="listActionEditing = message.id">
                                                                <PencilIcon class="h-3.5 w-3.5" />
                                                            </button>
                                                        </span>
                                                        with {{ message.listAction.creators?.length || 0 }} profile{{ (message.listAction.creators?.length || 0) !== 1 ? 's' : '' }}?
                                                    </p>
                                                    <div v-if="listActionEditing === message.id" class="mt-1.5 flex items-center gap-2">
                                                        <input
                                                            v-model="message.listAction.suggested_name"
                                                            class="rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            @keydown.enter="listActionEditing = null"
                                                        />
                                                        <Button variant="link" size="sm" class="h-auto p-0" @click="listActionEditing = null">Done</Button>
                                                    </div>
                                                </template>
                                                <!-- Delete prompt -->
                                                <template v-else-if="message.listAction.action === 'delete'">
                                                    <p class="text-sm text-gray-700">
                                                        Are you sure you want to delete the list "<strong>{{ message.listAction.list_name }}</strong>"? This cannot be undone.
                                                    </p>
                                                </template>
                                                <!-- Update add prompt -->
                                                <template v-else-if="message.listAction.operation === 'add'">
                                                    <p class="text-sm text-gray-700">
                                                        Add {{ message.listAction.creator_count || message.listAction.creators?.length || 0 }}
                                                        profile{{ (message.listAction.creator_count || message.listAction.creators?.length || 0) !== 1 ? 's' : '' }}
                                                        to "<strong>{{ message.listAction.list_name }}</strong>"?
                                                    </p>
                                                </template>
                                                <!-- Update remove prompt -->
                                                <template v-else-if="message.listAction.operation === 'remove'">
                                                    <p class="text-sm text-gray-700">
                                                        Remove {{ message.listAction.creator_count || message.listAction.creators?.length || 0 }}
                                                        profile{{ (message.listAction.creator_count || message.listAction.creators?.length || 0) !== 1 ? 's' : '' }}
                                                        from "<strong>{{ message.listAction.list_name }}</strong>"?
                                                    </p>
                                                </template>
                                                <div class="mt-2 flex gap-2">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span>
                                                                <Button
                                                                    :variant="listActionIsDestructive(message.listAction) ? 'destructive' : 'default'"
                                                                    size="sm"
                                                                    :disabled="message.listAction.action === 'create' && !message.listAction.suggested_name?.trim()"
                                                                    @click="confirmListAction(message)"
                                                                >
                                                                    {{ listActionConfirmLabel(message.listAction) }}
                                                                </Button>
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            v-if="message.listAction.action === 'create' && !message.listAction.suggested_name?.trim()"
                                                            side="top"
                                                            class="bg-gray-900 text-white px-2 py-1 rounded text-xs border border-gray-600"
                                                        >
                                                            List name is required
                                                        </TooltipContent>
                                                    </Tooltip>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        @click="cancelListAction(message)"
                                                    >
                                                        No Thanks
                                                    </Button>
                                                </div>
                                            </template>
                                        </Alert>

                                        <!-- Frozen Evaluation Table (persists in conversation history) -->
                                        <div v-if="message.evaluationTable?.length > 0 && (message.deconstruction?.deconstruction?.list_operation === 'refine' || message.listAction?.operation === 'remove')" class="mt-4">
                                            <div class="max-w-4xl overflow-hidden rounded-lg border border-gray-200 bg-white">
                                                <!-- Status bar -->
                                                <div class="border-b border-gray-200 bg-gray-50 px-4 py-3">
                                                    <div class="flex items-center space-x-2">
                                                        <CheckIcon class="h-4 w-4 text-green-600" />
                                                        <span class="text-sm font-medium text-gray-700">Evaluation complete</span>
                                                        <span class="ml-auto text-xs text-gray-500">
                                                            {{ message.evaluationTable.filter(c => c.decision === 'keep').length }} kept &middot;
                                                            {{ message.evaluationTable.filter(c => c.decision === 'remove').length }} removed
                                                        </span>
                                                    </div>
                                                </div>
                                                <!-- Table -->
                                                <div class="max-h-[32rem] overflow-y-auto">
                                                    <table class="w-full table-fixed text-sm">
                                                        <colgroup>
                                                            <col class="w-12">
                                                            <col class="w-40">
                                                            <col>
                                                        </colgroup>
                                                        <thead class="sticky top-0 border-b border-gray-200 bg-gray-50">
                                                            <tr>
                                                                <th class="px-4 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400"></th>
                                                                <th class="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Creator</th>
                                                                <th class="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Reasoning</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="divide-y divide-gray-100">
                                                            <tr v-for="creator in message.evaluationTable" :key="creator.id" class="group">
                                                                <!-- Status icon -->
                                                                <td class="px-4 py-1.5">
                                                                    <div v-if="creator.decision === 'keep'" class="flex h-4 w-4 items-center justify-center rounded-full bg-green-100">
                                                                        <CheckIcon class="h-2.5 w-2.5 text-green-600" />
                                                                    </div>
                                                                    <div v-else class="flex h-4 w-4 items-center justify-center rounded-full bg-red-100">
                                                                        <XIcon class="h-2.5 w-2.5 text-red-500" />
                                                                    </div>
                                                                </td>
                                                                <!-- Creator name -->
                                                                <td class="px-3 py-1.5">
                                                                    <span class="block truncate text-xs font-medium text-gray-900">{{ creator.name }}</span>
                                                                </td>
                                                                <!-- Reasoning -->
                                                                <td
                                                                    class="px-3 py-1.5"
                                                                    :class="{ 'cursor-pointer': creator.reasoning }"
                                                                    @click.stop="creator.reasoning && toggleCreatorReasoning(creator)"
                                                                >
                                                                    <span class="text-xs text-gray-500" :class="creator.expanded ? 'block' : 'block truncate'">{{ creator.reasoning || '' }}</span>
                                                                    <span v-if="creator.reasoning" class="text-xs text-gray-400 hover:text-gray-600" :class="creator.expanded ? 'inline' : 'hidden group-hover:inline'">{{ creator.expanded ? 'less ↑' : 'more ↓' }}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Real-time Thinking Box (while AI is thinking) -->
                                <div v-if="isLoading || isStreaming" class="mb-4">
                                    <!-- Basic Loading State (for non-streaming) -->
                                    <div
                                        v-if="!isStreaming"
                                        class="max-w-4xl overflow-hidden rounded-lg border border-gray-300 bg-gray-50"
                                    >
                                        <div class="border-b border-gray-300 bg-gray-100 px-4 py-3">
                                            <div class="flex items-center space-x-2">
                                                <Loader2Icon class="h-4 w-4 animate-spin text-blue-500" />
                                                <span class="text-sm font-medium text-gray-700">AI is searching...</span>
                                                <span class="ml-auto text-xs text-gray-500">
                                                    {{ $filters.humanizeDurationShort(requestTimer) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Real-time Thinking Display (wizard-style) -->
                                    <div
                                        v-else-if="isStreaming"
                                        class="max-w-4xl overflow-hidden rounded-lg border border-gray-300 bg-gray-50"
                                    >
                                        <!-- Thinking Header (clickable) -->
                                        <div
                                            class="cursor-pointer border-b border-gray-300 bg-gray-100 px-4 py-3 transition-colors hover:bg-gray-200"
                                            @click="toggleCurrentThinking"
                                        >
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center space-x-2">
                                                    <Loader2Icon class="h-4 w-4 animate-spin text-blue-500" />
                                                    <span class="text-sm font-medium text-gray-700">Thinking...</span>
                                                </div>
                                                <div class="flex items-center space-x-2">
                                                    <span class="text-xs text-gray-500"
                                                        >{{ currentThinkingSteps.length }} step{{ currentThinkingSteps.length !== 1 ? 's' : '' }}</span
                                                    >
                                                    <span class="text-xs text-gray-500">{{
                                                        $filters.humanizeDurationShort(requestTimer)
                                                    }}</span>
                                                    <AppIcon
                                                        name="chevron-down"
                                                        class="h-3 w-3 text-gray-500 transition-transform duration-200"
                                                        :class="{ 'rotate-180': !isCurrentThinkingExpanded }"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Collapsed Current Step Display (ChatGPT style) -->
                                        <div
                                            v-if="currentThinkingSteps.length > 0 && !isCurrentThinkingExpanded"
                                            class="px-4 py-3 border-b border-gray-200"
                                        >
                                            <div class="flex items-center space-x-3">
                                                <div class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500">
                                                    <div class="h-2 w-2 animate-pulse rounded-full bg-white"></div>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-sm text-gray-900 truncate">
                                                        {{ currentThinkingSteps[currentThinkingSteps.length - 1]?.step || 'Processing...' }}
                                                    </p>
                                                    <!-- Optional: Show step details for collapsed view -->
                                                    <div
                                                        v-if="currentThinkingSteps[currentThinkingSteps.length - 1]?.data?.count"
                                                        class="text-xs text-gray-500 mt-1"
                                                    >
                                                        Found: {{ currentThinkingSteps[currentThinkingSteps.length - 1].data.count }} results
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Real-time Steps with Wizard Timeline (Expanded View Only) -->
                                        <div
                                            v-if="currentThinkingSteps.length > 0 && isCurrentThinkingExpanded"
                                            class="p-4"
                                        >
                                            <div class="relative">
                                                <!-- Vertical connecting line (centered on circles) -->
                                                <div
                                                    v-if="currentThinkingSteps.length > 1"
                                                    class="absolute left-3 w-px bg-gray-300"
                                                    :style="{
                                                        top: '1.75rem',
                                                        height: `${(currentThinkingSteps.length - 1) * 2.5}rem`,
                                                    }"
                                                ></div>

                                                <div class="space-y-2">
                                                    <div
                                                        v-for="(step, index) in currentThinkingSteps"
                                                        :key="index"
                                                        class="animate-fade-in relative flex items-center space-x-4"
                                                    >
                                                        <!-- Step Circle -->
                                                        <div
                                                            class="relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-blue-500 bg-white"
                                                        >
                                                            <span class="text-xs font-medium text-blue-600">{{
                                                                index + 1
                                                            }}</span>
                                                        </div>

                                                        <!-- Step Content (no border, reduced height) -->
                                                        <div class="flex-1 py-1">
                                                            <p class="mb-1 text-sm text-gray-900">
                                                                {{ step.step }}
                                                            </p>

                                                            <!-- Step Details -->
                                                            <div
                                                                v-if="
                                                                    step.data &&
                                                                    (step.data.tools ||
                                                                        step.data.query ||
                                                                        step.data.count ||
                                                                        step.data.type)
                                                                "
                                                                class="space-y-1"
                                                            >
                                                                <div v-if="step.data.tools" class="text-xs text-gray-500">
                                                                    Tools: {{ step.data.tools.join(', ') }}
                                                                </div>
                                                                <div v-if="step.data.query" class="text-xs text-gray-500">
                                                                    Query: "{{ step.data.query }}"
                                                                </div>
                                                                <div v-if="step.data.count" class="text-xs text-gray-500">
                                                                    Found: {{ step.data.count }} results
                                                                </div>

                                                                <!-- Deconstruction Details -->
                                                                <div
                                                                    v-if="
                                                                        step.data.type === 'deconstruction' &&
                                                                        step.data.details
                                                                    "
                                                                    class="mt-2 text-xs"
                                                                >
                                                                    <div
                                                                        class="rounded border border-blue-200 bg-blue-50 p-2"
                                                                    >
                                                                        <div class="mb-1 font-semibold text-blue-800">
                                                                            Query Analysis:
                                                                        </div>
                                                                        <div
                                                                            v-if="
                                                                                step.data.details.success &&
                                                                                step.data.details.deconstruction
                                                                            "
                                                                            class="space-y-1 text-blue-700"
                                                                        >
                                                                            <div>
                                                                                <strong>Intent:</strong>
                                                                                {{
                                                                                    step.data.details.deconstruction.intent_classification?.replace(
                                                                                        '_',
                                                                                        ' ',
                                                                                    )
                                                                                }}
                                                                            </div>
                                                                            <div>
                                                                                <strong>Complexity:</strong>
                                                                                {{
                                                                                    step.data.details.deconstruction
                                                                                        .query_complexity
                                                                                }}
                                                                            </div>
                                                                            <div
                                                                                v-if="
                                                                                    step.data.details.deconstruction
                                                                                        .primary_requirements?.length
                                                                                "
                                                                            >
                                                                                <strong>Requirements:</strong>
                                                                                {{
                                                                                    step.data.details.deconstruction.primary_requirements.join(
                                                                                        ', ',
                                                                                    )
                                                                                }}
                                                                            </div>
                                                                            <div
                                                                                v-if="
                                                                                    step.data.details.deconstruction
                                                                                        .exclusions?.length
                                                                                "
                                                                            >
                                                                                <strong>Exclusions:</strong>
                                                                                {{
                                                                                    step.data.details.deconstruction.exclusions.join(
                                                                                        ', ',
                                                                                    )
                                                                                }}
                                                                            </div>
                                                                            <!-- Research Findings -->
                                                                            <div
                                                                                v-if="step.data.research_summaries?.length"
                                                                                class="mt-3 border-t border-blue-200 pt-2"
                                                                            >
                                                                                <strong class="text-blue-800">🔬 Research Conducted:</strong>
                                                                                <div v-for="(research, index) in step.data.research_summaries" :key="index" class="mt-2 rounded bg-blue-50 p-2">
                                                                                    <div class="font-medium text-blue-900">{{ research.topic }}</div>
                                                                                    <div v-if="research.key_findings?.length" class="mt-1 text-xs">
                                                                                        <strong>Key Findings:</strong>
                                                                                        <ul class="ml-3 mt-1 list-disc space-y-1">
                                                                                            <li v-for="finding in research.key_findings" :key="finding" class="text-blue-700">{{ finding }}</li>
                                                                                        </ul>
                                                                                    </div>
                                                                                    <div v-if="research.conclusion" class="mt-1 text-xs">
                                                                                        <strong>Conclusion:</strong>
                                                                                        <span class="text-blue-700">{{ research.conclusion }}</span>
                                                                                    </div>
                                                                                    <div class="mt-1 text-xs text-gray-500">
                                                                                        Research time: {{ Math.round(research.research_time_ms / 1000) }}s
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <!-- Plan Details -->
                                                                <div
                                                                    v-if="step.data.type === 'plan' && step.data.details"
                                                                    class="mt-2 text-xs"
                                                                >
                                                                    <div
                                                                        class="rounded border border-green-200 bg-green-50 p-2"
                                                                    >
                                                                        <div class="mb-1 font-semibold text-green-800">
                                                                            Search Strategy:
                                                                        </div>
                                                                        <div
                                                                            v-if="
                                                                                step.data.details.success &&
                                                                                step.data.details.plan
                                                                            "
                                                                            class="space-y-1 text-green-700"
                                                                        >
                                                                            <div>
                                                                                <strong>Approach:</strong>
                                                                                {{
                                                                                    step.data.details.plan.search_strategy?.replace(
                                                                                        '_',
                                                                                        ' ',
                                                                                    )
                                                                                }}
                                                                            </div>
                                                                            <div
                                                                                v-if="
                                                                                    step.data.details.plan.execution_steps
                                                                                        ?.length
                                                                                "
                                                                            >
                                                                                <strong>Steps:</strong>
                                                                                {{
                                                                                    step.data.details.plan.execution_steps
                                                                                        .length
                                                                                }}
                                                                                planned
                                                                            </div>
                                                                            <div
                                                                                v-if="
                                                                                    step.data.details.plan
                                                                                        .relevance_criteria?.length
                                                                                "
                                                                            >
                                                                                <strong>Success Criteria:</strong>
                                                                                {{
                                                                                    step.data.details.plan.relevance_criteria
                                                                                        .slice(0, 2)
                                                                                        .join(', ')
                                                                                }}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Live Creator Evaluation Table (list refinement) -->
                                <div v-if="evaluatingCreators.length > 0" class="mt-4">
                                    <div class="max-w-4xl overflow-hidden rounded-lg border border-gray-200 bg-white">
                                        <!-- Status bar -->
                                        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3">
                                            <div class="flex items-center space-x-2">
                                                <Loader2Icon v-if="isStreaming" class="h-4 w-4 animate-spin text-purple-500" />
                                                <CheckIcon v-else class="h-4 w-4 text-green-600" />
                                                <span class="text-sm font-medium text-gray-700">{{ isStreaming ? 'Evaluating creators...' : 'Evaluation complete' }}</span>
                                                <span class="ml-auto text-xs text-gray-500">
                                                    <template v-if="isStreaming">
                                                        {{ evaluatingCreators.filter(c => c.decision !== 'pending').length }} / {{ evaluatingCreators.length }} evaluated
                                                    </template>
                                                    <template v-else>
                                                        {{ evaluatingCreators.filter(c => c.decision === 'keep').length }} kept &middot;
                                                        {{ evaluatingCreators.filter(c => c.decision === 'remove').length }} removed
                                                    </template>
                                                </span>
                                            </div>
                                        </div>
                                        <!-- Table -->
                                        <div class="max-h-[36rem] overflow-y-auto">
                                            <table class="w-full table-fixed text-sm">
                                                <colgroup>
                                                    <col class="w-12">
                                                    <col class="w-40">
                                                    <col>
                                                </colgroup>
                                                <thead class="sticky top-0 border-b border-gray-200 bg-gray-50">
                                                    <tr>
                                                        <th class="px-4 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400"></th>
                                                        <th class="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Creator</th>
                                                        <th class="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Reasoning</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-100">
                                                    <tr v-for="creator in evaluatingCreators" :key="creator.id" class="group">
                                                        <!-- Status icon -->
                                                        <td class="px-4 py-1.5">
                                                            <div v-if="creator.decision === 'pending'" class="flex h-4 w-4 items-center justify-center rounded-full bg-gray-100">
                                                                <Loader2Icon class="h-2.5 w-2.5 animate-spin text-gray-400" />
                                                            </div>
                                                            <div v-else-if="creator.decision === 'keep'" class="flex h-4 w-4 items-center justify-center rounded-full bg-green-100">
                                                                <CheckIcon class="h-2.5 w-2.5 text-green-600" />
                                                            </div>
                                                            <div v-else class="flex h-4 w-4 items-center justify-center rounded-full bg-red-100">
                                                                <XIcon class="h-2.5 w-2.5 text-red-500" />
                                                            </div>
                                                        </td>
                                                        <!-- Creator name -->
                                                        <td class="px-3 py-1.5">
                                                            <span class="block truncate text-xs font-medium text-gray-900">{{ creator.name }}</span>
                                                        </td>
                                                        <!-- Reasoning -->
                                                        <td
                                                            class="px-3 py-1.5"
                                                            :class="{ 'cursor-pointer': creator.reasoning && creator.decision !== 'pending' }"
                                                            @click.stop="creator.reasoning && creator.decision !== 'pending' && toggleCreatorReasoning(creator)"
                                                        >
                                                            <span class="text-xs text-gray-500" :class="creator.expanded ? 'block' : 'block truncate'">{{ creator.reasoning || '' }}</span>
                                                            <span v-if="creator.reasoning && creator.decision !== 'pending'" class="text-xs text-gray-400 hover:text-gray-600" :class="creator.expanded ? 'inline' : 'hidden group-hover:inline'">{{ creator.expanded ? 'less ↑' : 'more ↓' }}</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>


                            </div>

                            <!-- Input -->

                            <!-- Bottom Input - Only show when conversation has started -->
                            <div
                                v-if="messages.length > 0"
                                class="flex-shrink-0 border-t border-gray-200 bg-gray-50 p-4 transition-all duration-300 ease-in-out"
                            >
                                <form @submit.prevent="handleSubmit">
                                    <!-- Input container with + button and send button inside -->
                                    <div class="flex flex-1 rounded-lg border border-gray-300 bg-white focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500">
                                        <!-- Add List Button -->
                                        <div class="flex-shrink-0 self-end pb-1.5 pl-1.5">
                                            <DropdownMenu v-model:open="isConversationListDropdownOpen">
                                                <DropdownMenuTrigger class="flex h-9 w-9 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-accent hover:text-gray-600 focus:outline-none">
                                                    <PlusIcon class="h-5 w-5" />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start" class="w-[220px]">
                                                    <DropdownMenuLabel>Attach a List</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />
                                                    <div v-if="isLoadingLists" class="px-2 py-3 text-center text-sm text-gray-500">
                                                        <Loader2Icon class="mx-auto h-4 w-4 animate-spin" />
                                                    </div>
                                                    <div v-else-if="!userLists || userLists.length === 0" class="px-2 py-2">
                                                        <p class="text-sm text-gray-500 mb-2">You have no lists yet.</p>
                                                        <router-link
                                                            :to="{ name: 'lists' }"
                                                            class="text-sm text-blue-500 hover:text-blue-700 font-medium"
                                                        >
                                                            Click here to create one
                                                        </router-link>
                                                    </div>
                                                    <template v-else>
                                                        <div v-if="availableLists.length > 0" class="sticky top-0 bg-white px-2 py-1.5">
                                                            <input
                                                                v-model="listSearchText"
                                                                type="text"
                                                                placeholder="Search lists…"
                                                                class="w-full rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                                                @click.stop
                                                                @keydown.stop
                                                            />
                                                        </div>
                                                        <div v-if="availableLists.length === 0" class="px-2 py-2">
                                                            <p class="text-sm text-gray-500">All lists attached.</p>
                                                        </div>
                                                        <div v-else-if="filteredAvailableLists.length === 0" class="px-2 py-2">
                                                            <p class="text-sm text-gray-500">No matching lists.</p>
                                                        </div>
                                                        <div v-else class="max-h-[280px] overflow-y-auto">
                                                            <DropdownMenuItem
                                                                v-for="list in filteredAvailableLists"
                                                                :key="list.id"
                                                                class="cursor-pointer"
                                                                @click="handleListSelect(list)"
                                                            >
                                                                <span class="truncate">{{ list.name }}</span>
                                                            </DropdownMenuItem>
                                                        </div>
                                                    </template>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        <div class="flex-1 min-w-0 flex flex-col">
                                            <div v-if="selectedLists.length > 0" class="flex flex-wrap gap-1.5 pt-2 pr-2">
                                                <Badge
                                                    v-for="list in selectedLists"
                                                    :key="list.id"
                                                    variant="secondary"
                                                    class="gap-1 pl-1.5"
                                                >
                                                    <button
                                                        type="button"
                                                        class="flex items-center justify-center h-3.5 w-3.5 rounded-full hover:bg-gray-300 transition-colors"
                                                        @click="handleListRemove(list.id)"
                                                    >
                                                        <XIcon class="h-2.5 w-2.5" />
                                                    </button>
                                                    {{ list.name }}
                                                </Badge>
                                            </div>
                                            <textarea
                                                ref="autoGrowConversation"
                                                v-model="input"
                                                v-auto-grow
                                                rows="1"
                                                placeholder="Ask me to find more influencers…"
                                                :disabled="isLoading"
                                                class="w-full resize-none overflow-hidden border-0 bg-transparent py-3 pr-4 text-slate-900 focus:outline-none focus:ring-0"
                                                @keydown.enter.exact.prevent="handleSubmit"
                                            />
                                        </div>
                                        <!-- Send Button inside box -->
                                        <div class="flex-shrink-0 self-end pb-1.5 pr-1.5">
                                            <Button
                                                type="submit"
                                                size="icon"
                                                class="h-9 w-9 rounded-lg"
                                                :disabled="isLoading || !input.trim()"
                                            >
                                                <SendIcon class="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                </div>
            </template>
        </SidebarLayout>

        <!-- Creators Modal -->
        <Dialog :open="isCreatorsModalOpen" @update:open="isCreatorsModalOpen = $event">
            <DialogContent
                class="fixed inset-1 z-50 flex !w-auto !max-w-none !translate-x-0 !translate-y-0 !transform-none flex-col overflow-y-auto lg:inset-[5rem]"
            >
                <DialogHeader class="">
                    <DialogTitle class="text-xl font-bold text-gray-900">Matched {{ selectedMessageForModal.creator_count }} Creator{{ selectedMessageForModal.creator_count > 0 ? "s" : "" }}</DialogTitle>
                </DialogHeader>

                <template v-if="isCreatorsModalLoading">
                    <SkeletonAIInfluencerResults />
                </template>

                <template v-else>
                    <!-- Tab Navigation with View Toggle -->
                    <div>
                        <div class="flex items-center justify-between">
                            <nav class="flex space-x-8">
                                <button
                                    v-if="isDevModeEnabled"
                                    :class="[
                                        'border-b-2 px-1 py-2 text-sm font-medium',
                                        activeTab === 'accepted'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    ]"
                                    @click="activeTab = 'accepted'"
                                >
                                    Matched Creators
                                    <span
                                        class="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                                    >
                                        {{ modalAcceptedPaging.totalRecords }}
                                    </span>
                                </button>
                                <button
                                    v-if="isDevModeEnabled"
                                    :class="[
                                        'border-b-2 px-1 py-2 text-sm font-medium',
                                        activeTab === 'rejected'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    ]"
                                    @click="activeTab = 'rejected'"
                                >
                                    Rejected Creators
                                    <span class="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                                        {{ modalRejectedPaging.totalRecords}}
                                    </span>
                                </button>
                            </nav>

                            <!-- View Toggle -->
                            <div class="flex items-center space-x-1 rounded-lg bg-gray-100 p-1">
                                <button
                                    :class="[
                                        'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                                        resultsView === 'cards'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900',
                                    ]"
                                    @click="resultsView = 'cards'"
                                >
                                    <GridIcon class="mr-1.5 inline h-4 w-4" />
                                    Cards
                                </button>
                                <button
                                    :class="[
                                        'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                                        resultsView === 'table'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900',
                                    ]"
                                    @click="resultsView = 'table'"
                                >
                                    <TableIcon class="mr-1.5 inline h-4 w-4" />
                                    Table
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Results Content -->
                    <div class="h-full overflow-y-auto">
                        <!-- Table View -->
                        <DiscoverTable
                            v-if="currentTabCreators && resultsView === 'table'"
                            :creators="currentTabCreators"
                            :columns="tableColumns"
                            :enabled-columns="enabledColumns"
                            :is-loading="isCreatorsLoading"
                            :is-selectable="true"
                            :platform-id="getPrimaryPlatform(currentTabCreators || [])"
                            :include-all-platforms-by-default-for-list="true"
                            :superview-path="'superview'"
                            :show-pre-generation-slot="false"
                            :paging="currentTabPaging"
                            :is-sortable="false"
                            @add-to-list="handleAddToList"
                            @add-to-campaign="handleAddToCampaign"
                            @filter-change="onFilterChange"
                            @export-creators="onExportCreators"
                            @paging-change="onPagingChange"
                        >
                            <template #header-toolbar>
                                <div class="flex items-center space-x-2">
                                    <button
                                        class="flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                                        @click="onExportCreators"
                                    >
                                        <FileUp class="mr-2 h-4 w-4" />
                                        Export
                                    </button>
                                    <ColumnDisplayManager
                                        v-model="enabledColumns"
                                        :available-columns="tableColumns"
                                        :groups="availableColumnGroups"
                                        @update:model-value="onEnabledColumnChange"
                                    />
                                </div>
                            </template>

                            <template #creator="{ field }">
                                <DiscoveryProfileUnlock
                                    :platform-id="field.platform_id"
                                    :platform-user-id="field.id"
                                    :modal="'creator'"
                                    :name="field.display_name"
                                    :display-name="field.display_name"
                                />
                            </template>

                            <template #platforms="{ field }">
                                <div class="flex space-x-2">
                                    <div
                                        v-for="source in $lodash.uniqBy(field, 'platform_id')"
                                        :key="source.id"
                                        class="rounded p-1"
                                    >
                                        <AppIcon
                                            :name="source.platform_id === 'twitter' ? 'x-twitter' : source.platform_id"
                                            class="h-4 w-4"
                                            :class="source.platform_id === 'twitter' ? 'text-x' : `text-${source.platform_id}`"
                                        />
                                    </div>
                                </div>
                            </template>
                            <template #affinity="{ item }">
                                <div class="text-sm text-gray-600">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div class="inline-block">
                                                <AffinityPlate :affinity-percent="getRelevanceScore(item) / 10" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent class="max-w-xs">
                                            <div class="mb-1 font-semibold text-white">Recommendation</div>
                                            <p>
                                                {{
                                                    getReasoningData(item)?.recommendation || 'No recommendation available'
                                                }}
                                            </p>

                                            <div class="mb-1 mt-2 font-semibold text-white">Reason for match</div>
                                            <ul class="list-disc px-5">
                                                <li
                                                    v-for="reason in getReasoningData(item)?.match_reasons || []"
                                                    :key="reason"
                                                >
                                                    {{ reason }}
                                                </li>
                                            </ul>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </template>
                        </DiscoverTable>

                        <!-- Cards View -->
                        <CreatorCardsView
                            v-if="currentTabCreators && resultsView === 'cards'"
                            :creators="currentTabCreators"
                            :is-loading="isCreatorsLoading"
                            :paging="currentTabPaging"
                            @paging-change="onPagingChange"
                        />
                    </div>
                </template>
            </DialogContent>
        </Dialog>

    </TooltipProvider>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { axios } from '@/utils/axios'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { ref, computed, onMounted, watch, onUnmounted, nextTick } from 'vue'
import { SidebarLayout } from '@/components/ui/layouts'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/components/ui/alert'
import { toast } from '@/components/ui/toast/use-toast.ts'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import { useAiChatState } from '@/composables/useAiChatState'
import DiscoveryPagination from '@/partials/tables/discovery/DiscoveryPagination.vue'
import AffinityPlate from '@/components/plates/AffinityPlate.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import ConversationHistory from '@/components/ConversationHistory.vue'
import CreatorResults from '@/components/CreatorResults.vue'
import DiscoveryProfileUnlock from '@/components/unlocks/DiscoveryProfileUnlock.vue'
import CreatorCardsView from '@/components/CreatorCardsView.vue'
import { renderMarkdown } from '@/utils/markdown'
import SkeletonAIChatMessages from '@/partials/skeletons/SkeletonAIChatMessages.vue'
import SkeletonAIInfluencerResults from '@/partials/skeletons/SkeletonAIInfluencerResults.vue'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'
import {
    UsersIcon,
    BotIcon,
    SendIcon,
    Loader2Icon,
    SearchIcon,
    TrendingUpIcon,
    MailIcon,
    ExternalLinkIcon,
    DatabaseIcon,
    FilterIcon,
    StarIcon,
    EyeIcon,
    HeartIcon,
    MessageCircleIcon,
    GlobeIcon,
    MapPinIcon,
    CalendarIcon,
    AwardIcon,
    AlertTriangleIcon,
    ClockIcon,
    WifiOffIcon,
    LockIcon,
    RefreshCwIcon,
    XIcon,
    ServerIcon,
    ChevronRightIcon,
    ClipboardIcon,
    CheckIcon,
    PencilIcon,
    Table2Icon as TableIcon,
    Grid3x3Icon as GridIcon,
    FileUp,
    Plus as PlusIcon,
    // Thumbs icons are exported as ThumbsUp/ThumbsDown in lucide
    ThumbsUp as ThumbsUpIcon,
    ThumbsDown as ThumbsDownIcon,
} from 'lucide-vue-next'

export default {
    name: 'AiChatPage',

    metaInfo: {
        title: 'AI Chat - Influencer Search',
    },

    directives: {
        autoGrow: {
            mounted(el, binding) {
                const parentEl = binding.instance.textareaParent

                if (!parentEl) {
                    console.warn('v-auto-grow: no textareaParent ref found')
                    return
                }

                const max = parentEl.clientHeight / 2
                el.style.overflowY = 'auto'
                el.style.maxHeight = max + 'px'

                const resize = () => {
                    el.style.height = 'auto'
                    el.style.height = Math.min(el.scrollHeight, max) + 'px'
                }

                el.addEventListener('input', resize)
                resize()
            },
        },
    },

    setup() {
        const store = useStore()
        const route = useRoute()
        const router = useRouter()

        // User personalization
        const firstName = computed(() => store.state.self.operator.first_name || null)

        const isDevModeEnabled = computed(() => store.state.self.isDevModeEnabled || false)

        const {
            messages,
            conversations,
            currentConversationId,
            isLoading,
            isStreaming,
            currentThinkingSteps,
            currentStreamController,
            inFlightConversationKey,
            messagesByConversationKey,
            selectedListsByConversationId,
        } = useAiChatState()

        // Chat state
        const messagesContainer = ref(null)
        const textareaParent = ref(null)
        const autoGrowWelcome = ref(null)
        const autoGrowConversation = ref(null)
        const input = ref('')
        const error = ref(null)
        const creatorAffinity = ref({})
        const loadingReasoning = ref({})
        const retryingMessage = ref(null)
        const retryCountdowns = ref({})
        const feedbackSubmitting = ref(false)

        // Live creator evaluation state (for list refinement streaming)
        const evaluatingCreators = ref([])

        // Message interaction state
        const editingMessageId = ref(null)
        const editingContent = ref('')

        // Conversation state
        const isLoadingConversations = ref(false)
        const isLoadingPreviousConversation = ref(false)
        const isLoadingMoreConversations = ref(false)
        const conversationPageLimit = 50
        const conversationSkip = ref(0)
        const hasMoreConversations = ref(true)

        // Modal state
        const isCreatorsModalOpen = ref(false)
        const isCreatorsModalLoading = ref(false)
        const selectedMessageForModal = ref(null)
        const selectedAcceptedCreators = ref([])
        const selectedRejectedCreators = ref([])
        const isCreatorsLoading = ref(false)
        const activeTab = ref('accepted') // 'accepted' or 'rejected'
        const resultsView = ref('cards') // 'table' or 'cards'

        // Timer state
        const requestTimer = ref(0)
        const requestStartTime = ref(null)
        const timerInterval = ref(null)

        // Copy button state
        const copyButtonState = ref('idle') // 'idle', 'copying', 'success', 'error'

        // Streaming state
        const isCurrentThinkingExpanded = ref(false) // Whether current thinking steps are expanded (default collapsed)
        const isAwaitingClarification = ref(false)

        const collapsedThinkingSteps = ref(new Set()) // Set of message IDs with collapsed thinking steps
        // Abort controller stored in shared state

        // Auto-scroll state
        const isUserScrolledUp = ref(false) // Whether user has manually scrolled up from bottom

        // Lists state
        const userLists = ref([])
        const isLoadingLists = ref(false)
        const isWelcomeListDropdownOpen = ref(false)
        const isConversationListDropdownOpen = ref(false)
        const selectedLists = ref([])

        // List action state per message: { [messageId]: 'creating' | 'created' }
        const listActionState = ref({})
        const listActionEditing = ref(null) // message ID currently editing the list name

        const listSearchText = ref('')

        const availableLists = computed(() => {
            const selectedIds = new Set(selectedLists.value.map(l => l.id))
            return userLists.value.filter(l => !selectedIds.has(l.id))
        })

        const filteredAvailableLists = computed(() => {
            if (!listSearchText.value.trim()) return availableLists.value
            const search = listSearchText.value.trim().toLowerCase()
            return availableLists.value.filter(l => l.name.toLowerCase().includes(search))
        })

        const sanitizeAssistantContent = (content) => {
            if (!content) return content
            // Match both [INTERNAL ...] and [SYSTEM INTERNAL ...] patterns
            const withoutInternal = content.replace(/\[(?:SYSTEM\s+)?INTERNAL[\s\S]*?\]/gi, '')

            const sanitized = withoutInternal.replace(/^(?:\s*(?:instagram|tiktok|youtube|twitter|x|facebook):[^\s|]+(?:\|(?:instagram|tiktok|youtube|twitter|x|facebook):[^\s|]+)*)\s*/i, '')
            return sanitized.trim()
        }

        const getCachedConversationTitle = (conversationId) => {
            if (!conversationId) return null
            try {
                return localStorage.getItem(`ai-chat-title:${conversationId}`)
            } catch (error) {
                return null
            }
        }

        const cacheConversationTitle = (conversationId, title) => {
            if (!conversationId || !title) return
            try {
                localStorage.setItem(`ai-chat-title:${conversationId}`, title)
            } catch (error) {
                // Ignore storage errors (private mode, quota, etc.)
            }
        }

        const persistConversationTitle = async (conversationId, title) => {
            if (!conversationId || !title) return
            try {
                cacheConversationTitle(conversationId, title)
                const response = await axios.put(`/ai/conversations/${conversationId}/title`, {
                    title,
                })
                if (response.data.success) {
                    const conversation = conversations.value.find((c) => c._id === conversationId)
                    if (conversation) {
                        conversation.title = title
                    }
                }
            } catch (error) {
                console.error('Failed to persist conversation title:', error)
            }
        }

        const applyConversationTitle = async (conversationKey, title) => {
            if (!conversationKey || !title) return
            const normalizedTitle = title.trim().toLowerCase()
            if (normalizedTitle.includes('clarified creator search')) return

            const conversation = conversations.value.find((c) => c._id === conversationKey)
            if (conversation) {
                conversation.title = title
            }

            if (conversationKey === window.tempConversationId) {
                window.pendingConversationTitle = title
                return
            }

            await persistConversationTitle(conversationKey, title)
        }

        const getActiveConversationKey = () => {
            return currentConversationId.value || window.tempConversationId || null
        }

        const getConversationMessages = (conversationKey) => {
            if (!conversationKey) return []
            return messagesByConversationKey.value[conversationKey] || []
        }

        const setConversationMessages = (conversationKey, nextMessages) => {
            if (!conversationKey) return
            messagesByConversationKey.value = {
                ...messagesByConversationKey.value,
                [conversationKey]: nextMessages,
            }

            if (conversationKey === getActiveConversationKey()) {
                messages.value = nextMessages
            }
        }

        const migrateConversationMessages = (fromKey, toKey) => {
            if (!fromKey || !toKey || fromKey === toKey) return
            const existing = messagesByConversationKey.value[fromKey]
            if (!existing) return

            const nextMessagesByConversationKey = { ...messagesByConversationKey.value }
            delete nextMessagesByConversationKey[fromKey]
            nextMessagesByConversationKey[toKey] = existing
            messagesByConversationKey.value = nextMessagesByConversationKey
        }

        const modalAcceptedPaging = ref({
            totalPages: 0,
            totalRecords: 0,
            perPage: 25,
            currentPage: 1,
            sort: [],
            filters:  [
                { 'id' : 'decision', 'value' : 'accepted'}
            ]
        })

        const modalRejectedPaging = ref({
            totalPages: 0,
            totalRecords: 0,
            perPage: 25,
            currentPage: 1,
            sort: [],
            filters:  [
                { 'id' : 'decision', 'value' : 'rejected'}
            ]
        })

        // Auto-scroll utility functions
        const scrollToBottom = (smooth = true) => {
            if (!messagesContainer.value) return

            const container = messagesContainer.value
            const scrollOptions = {
                top: container.scrollHeight,
                behavior: smooth ? 'smooth' : 'auto',
            }
            container.scrollTo(scrollOptions)
        }

        const isScrolledToBottom = () => {
            if (!messagesContainer.value) return true

            const container = messagesContainer.value
            const threshold = 5 // pixels tolerance
            return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold
        }

        const handleScroll = () => {
            if (!messagesContainer.value) return

            // Check if user manually scrolled up from bottom
            isUserScrolledUp.value = !isScrolledToBottom()
        }

        // Creator link parsing and handling
        // Format: [[creator:platform1:id1|platform2:id2|...|DisplayName]]
        const parseCreatorLinks = (content) => {
            if (!content) return content
            // Match [[creator:...]] pattern and parse the contents
            return content.replace(
                /\[\[creator:([^\]]+)\]\]/g,
                (match, innerContent) => {
                    // Split by | to get segments
                    const segments = innerContent.split('|')
                    // Last segment is the display name
                    const displayName = segments[segments.length - 1]
                    // Everything else are platform:id pairs
                    const platformIds = segments.slice(0, -1).join('|')

                    return `<span class="creator-link cursor-pointer text-blue-600 hover:text-blue-800 hover:underline" data-platform-ids="${platformIds}">${displayName}</span>`
                }
            )
        }

        const renderMessageContent = (content) => {
            if (!content) return ''
            // First parse creator links, then render markdown
            const withCreatorLinks = parseCreatorLinks(content)
            return renderMarkdown(withCreatorLinks)
        }

        const handleCreatorLinkClick = (event, message) => {
            const target = event.target
            if (!target.classList.contains('creator-link')) return

            const platformIdsStr = target.dataset.platformIds
            if (!platformIdsStr) return

            // Parse platform:id pairs (e.g., "twitch:219893795|twitter:722158486038163456")
            const platformPairs = platformIdsStr.split('|').map(pair => {
                const [platform, id] = pair.split(':')
                return { platform, id }
            }).filter(p => p.platform && p.id)

            if (platformPairs.length === 0) {
                console.warn('No valid platform IDs found:', platformIdsStr)
                return
            }

            // Try to find a matching creator in the message's creator_sample
            const creators = message.creator_sample || []
            let matchedCreator = null
            let matchedPlatform = null

            // Try each platform:id pair to find a match
            for (const { platform, id } of platformPairs) {
                matchedCreator = creators.find(c => {
                    // Check source_ids for matching platform and id
                    if (c.source_ids) {
                        return c.source_ids.some(source =>
                            source.platform_id === platform && source.id === id
                        )
                    }
                    // Fallback: check superview
                    if (c.superview?.platform_id === platform && c.superview?._id === id) {
                        return true
                    }
                    return false
                })

                if (matchedCreator) {
                    matchedPlatform = { platform, id }
                    break
                }
            }

            // If no match in creator_sample, just use the first available platform
            if (!matchedPlatform) {
                matchedPlatform = platformPairs[0]
            }

            // Open the creator profile modal
            router.push({
                path: route.path,
                query: {
                    ...route.query,
                    modal: 'creator',
                    platform_id: matchedPlatform.platform,
                    platform_user_id: matchedPlatform.id,
                },
            })
        }

         const getModalAcceptedCreators = (messageId) => {
            const body = {
                paging: modalAcceptedPaging.value,
            }
            return axios
                .post(`/ai/conversations/${currentConversationId.value}/messages/${messageId}/creators`, body)
                .then(({data}) => {
                    selectedAcceptedCreators.value = transformCreatorsForTable(data.payload.records)
                    modalAcceptedPaging.value.currentPage = data.payload.paging.currentPage
                    modalAcceptedPaging.value.totalPages = Math.ceil(data.payload.paging.totalRecords / data.payload.paging.perPage)

                    modalAcceptedPaging.value.totalRecords = data.payload.paging.totalRecords
                })
        }

        const getModalRejectedCreators = (messageId) => {
            const body = {
                paging: modalRejectedPaging.value,
            }
            return axios
                .post(`/ai/conversations/${currentConversationId.value}/messages/${messageId}/creators`, body)
                .then(({data}) => {
                    selectedRejectedCreators.value = transformCreatorsForTable(data.payload.records)
                    modalRejectedPaging.value.currentPage = data.payload.paging.currentPage
                    modalRejectedPaging.value.totalPages = Math.ceil(data.payload.paging.totalRecords / data.payload.paging.perPage)

                    modalRejectedPaging.value.totalRecords = data.payload.paging.totalRecords
                })
        }

        const resetCreatorsPaging = () => {
            modalAcceptedPaging.value.currentPage = 1
            modalRejectedPaging.value.currentPage = 1
        }

        const openCreatorsModal = async (message) => {
            selectedMessageForModal.value = message
            isCreatorsModalOpen.value = true
            isCreatorsModalLoading.value = true
            resetCreatorsPaging()
            await Promise.all([
                getModalAcceptedCreators(message.id),
                getModalRejectedCreators(message.id)
            ])

            if ((!selectedAcceptedCreators.value || selectedAcceptedCreators.value.length === 0) && selectedRejectedCreators.value && selectedRejectedCreators.value.length > 0) {
                activeTab.value = 'rejected'
            } else {
                activeTab.value = 'accepted'
            }

            isCreatorsModalLoading.value = false
        }

        // Computed property for current tab's creators (for table)
        const currentTabCreators = computed(() => {
            return activeTab.value === 'accepted' ? selectedAcceptedCreators.value : selectedRejectedCreators.value
        })

        const currentTabPaging = computed(() => {
            return activeTab.value === 'accepted' ? modalAcceptedPaging.value : modalRejectedPaging.value
        })


        const onFilterChange = async (updatedSort) => {
            isCreatorsLoading.value = true
            if (activeTab.value === 'accepted') {
                _.assign(modalAcceptedPaging.value.sort, updatedSort)
                await getModalAcceptedCreators(selectedMessageForModal.value.id)
            } else {
                _.assign(modalRejectedPaging.value.sort, updatedSort)
                await getModalRejectedCreators(selectedMessageForModal.value.id)
            }
            isCreatorsLoading.value = false
        }

        const onPagingChange = async(updatedPaging) => {
            isCreatorsLoading.value = true
            if (activeTab.value === 'accepted') {
                _.assign(modalAcceptedPaging.value, updatedPaging)
                await getModalAcceptedCreators(selectedMessageForModal.value.id)
            } else {
                _.assign(modalRejectedPaging.value, updatedPaging)
                await getModalRejectedCreators(selectedMessageForModal.value.id)
            }
            isCreatorsLoading.value = false
        }


        // Get primary platform from creators
        const getPrimaryPlatform = (creators) => {
            // First check if we have a searched platform from the search context
            const searchedPlatform = selectedMessageForModal.value?.searchContext?.platform_id
            if (searchedPlatform) {
                return searchedPlatform
            }

            // Fallback to existing logic
            if (!creators || creators.length === 0) return 'twitch' // default

            // Get the first creator's platform from superview or source_ids
            const firstCreator = creators[0]

            // Try superview first (backend-joined data)
            if (firstCreator?.superview?.normalized?.platform_id) {
                return firstCreator.superview.normalized.platform_id
            }

            // Fallback to source_ids (original data)
            if (firstCreator?.source_ids && firstCreator.source_ids.length > 0) {
                return firstCreator.source_ids[0].platform_id
            }

            return 'twitch' // default fallback
        }

        // Timer functions
        const startTimer = () => {
            requestStartTime.value = moment()
            requestTimer.value = 0

            timerInterval.value = setInterval(() => {
                if (requestStartTime.value) {
                    requestTimer.value = moment().diff(requestStartTime.value)
                }
            }, 100) // Update every 100ms for smooth display
        }

        const stopTimer = () => {
            if (timerInterval.value) {
                clearInterval(timerInterval.value)
                timerInterval.value = null
            }
            requestStartTime.value = null
        }

        // Error handling utilities
        const ERROR_ICONS = {
            validation_error: AlertTriangleIcon,
            rate_limit_error: ClockIcon,
            api_quota_error: ServerIcon,
            database_error: DatabaseIcon,
            file_system_error: DatabaseIcon,
            network_error: WifiOffIcon,
            timeout_error: ClockIcon,
            authentication_error: LockIcon,
            permission_error: LockIcon,
            search_error: SearchIcon,
            ai_model_error: ServerIcon,
            tool_execution_error: AlertTriangleIcon,
            internal_error: AlertTriangleIcon,
        }

        const ERROR_TITLES = {
            validation_error: 'Input Error',
            rate_limit_error: 'Too Many Requests',
            api_quota_error: 'Service Limit Reached',
            database_error: 'Service Temporarily Unavailable',
            file_system_error: 'Configuration Error',
            network_error: 'Connection Issue',
            timeout_error: 'Request Timed Out',
            authentication_error: 'Authentication Required',
            permission_error: 'Permission Denied',
            search_error: 'Search Unavailable',
            ai_model_error: 'AI Service Unavailable',
            tool_execution_error: 'Search Error',
            internal_error: 'System Error',
        }

        const getErrorIcon = (errorType) => {
            return ERROR_ICONS[errorType] || AlertTriangleIcon
        }

        const getErrorTitle = (errorType) => {
            return ERROR_TITLES[errorType] || 'Error'
        }

        // Suggested queries
        const suggestedQueries = [
            'Find me Instagram lifestyle influencers in California with over 50k followers',
            'Show me TikTok creators who make cooking content',
            'Find fashion influencers on Instagram who work with beauty brands',
            'Find fitness YouTubers with over 100k subscribers who do product reviews',
            'Show me travel influencers on Instagram based in Europe',
        ]

        const formatNumber = (num) => {
            if (num >= 1000000) {
                return `${(num / 1000000).toFixed(1)}M`
            } else if (num >= 1000) {
                return `${(num / 1000).toFixed(1)}K`
            }
            return num?.toLocaleString() || 'N/A'
        }

        const getAllPlatforms = (creator) => {
            const platforms = []

            // Try new format first - check if creator has source_ids and platform_metrics
            if (creator.source_ids && Array.isArray(creator.source_ids)) {
                creator.source_ids.forEach((source) => {
                    if (source.platform_metrics) {
                        platforms.push({
                            platform: source.platform_id.charAt(0).toUpperCase() + source.platform_id.slice(1),
                            contentType: source.platform_id,
                            data: source.platform_metrics,
                            key: source.platform_id,
                        })
                    }
                })
            }

            // Fallback to old format - check creator.metrics
            if (platforms.length === 0 && creator.metrics) {
                for (const [key, data] of Object.entries(creator.metrics)) {
                    if (data && typeof data === 'object' && data.followers) {
                        const platform = key.split('_')[0]
                        platforms.push({
                            platform: platform.charAt(0).toUpperCase() + platform.slice(1),
                            contentType: key,
                            data: data,
                            key: key,
                        })
                    }
                }
            }

            // Additional fallback - check direct properties
            if (platforms.length === 0 && creator.followers) {
                platforms.push({
                    platform: 'Unknown',
                    contentType: 'direct',
                    data: {
                        followers: creator.followers,
                        country: creator.country,
                    },
                    key: 'direct',
                })
            }

            return platforms.sort((a, b) => (b.data.followers || 0) - (a.data.followers || 0))
        }

        const getBestMetrics = (creator) => {
            const platforms = getAllPlatforms(creator)
            if (platforms.length === 0) return null
            return platforms[0]
        }

        // Helper functions for new data format
        const getRelevanceScore = (creator) => {
            // Try multiple possible locations for relevance score
            return (
                creator.reasoning?.relevance_score ||
                creator.relevance_score ||
                creator.ai_analysis?.relevance_score ||
                creator.match_score ||
                0
            )
        }

        const getReasoningData = (creator) => {
            // Try multiple possible locations for reasoning data
            return creator.reasoning || creator.ai_analysis || creator.match_reasoning || null
        }

        const paging = ref({
            perPage: 15,
            currentPage: 1,
            totalRecords: 0,
            totalPages: 1,
            sort: [
                {
                    field: 'affinity',
                    direction: 'desc',
                    source: 'default',
                },
            ],
        })

        // Computed property for sorted creators
        const sortedCreators = computed(() => {
            if (!selectedMessageForModal.value || !selectedMessageForModal.value.creators) {
                return []
            }

            let creators = transformCreatorsForTable(selectedMessageForModal.value.creators)

            // Apply sorting if there's a sort configuration
            if (paging.value.sort && paging.value.sort.length > 0) {
                const sortConfig = paging.value.sort[0]
                const { field, direction } = sortConfig

                creators = _.orderBy(
                    creators,
                    [
                        (creator) => {
                            switch (field) {
                                case 'creator':
                                    return (
                                        creator.superview?.normalized?.display_name ||
                                        creator.display_name ||
                                        creator.name ||
                                        ''
                                    )
                                case 'followers':
                                case 'location':
                                    return (
                                        creator.ai_analysis?.country_code ||
                                        getBestMetrics(creator)?.data?.country ||
                                        ''
                                    )
                                case 'relevance_score':
                                    return getRelevanceScore(creator)
                                default:
                                    return creator[field] || ''
                            }
                        },
                    ],
                    [direction],
                )
            }

            return creators
        })

        // Computed property for sorted rejected creators
        const sortedRejectedCreators = computed(() => {
            if (!selectedMessageForModal.value || !selectedMessageForModal.value.rejectedCreators) {
                return []
            }
            let creators = transformCreatorsForTable(selectedMessageForModal.value.rejectedCreators)
            // Apply sorting if there's a sort configuration
            if (paging.value.sort && paging.value.sort.length > 0) {
                const sortConfig = paging.value.sort[0]
                const { field, direction } = sortConfig
                creators = _.orderBy(
                    creators,
                    [
                        (creator) => {
                            switch (field) {
                                case 'creator':
                                    return (
                                        creator.superview?.normalized?.display_name ||
                                        creator.display_name ||
                                        creator.name ||
                                        ''
                                    )
                                case 'followers':
                                    return getBestMetrics(creator)?.data?.followers || 0
                                case 'location':
                                    return (
                                        creator.ai_analysis?.country_code ||
                                        getBestMetrics(creator)?.data?.country ||
                                        ''
                                    )
                                case 'relevance_score':
                                    return getRelevanceScore(creator)
                                default:
                                    return creator[field] || ''
                            }
                        },
                    ],
                    [direction],
                )
            }
            return creators
        })


        // Computed property for raw creators (for cards)
        const currentTabRawCreators = computed(() => {
            if (!selectedMessageForModal.value) {
                return []
            }

            const rawCreators =
                activeTab.value === 'accepted'
                    ? selectedMessageForModal.value.creators || []
                    : selectedMessageForModal.value.rejectedCreators || []

            // Apply same sorting logic as the table but on raw data
            if (paging.value.sort && paging.value.sort.length > 0) {
                const sortConfig = paging.value.sort[0]
                const { field, direction } = sortConfig

                return _.orderBy(
                    rawCreators,
                    [
                        (creator) => {
                            switch (field) {
                                case 'creator':
                                    return creator.display_name || creator.name || ''
                                case 'followers':
                                    return creator.total_followers || 0
                                case 'location':
                                    return creator.location || creator.country || ''
                                case 'relevance_score':
                                    return getRelevanceScore(creator)
                                default:
                                    return creator[field] || ''
                            }
                        },
                    ],
                    [direction === 'DESC' ? 'desc' : 'asc'],
                )
            }

            return rawCreators.filter((creator) => creator && creator._id)
        })

        // Handle filter change event from DiscoverTable
        const handleFilterChange = () => {
            // The sorting is handled by the computed property
            // This function exists to satisfy the event handler requirement
        }

        const transformCreatorsForTable = (creators) => {
            if (!creators || !Array.isArray(creators)) {
                return []
            }

            // Get the searched platform from search context
            const searchedPlatform = selectedMessageForModal.value?.searchContext?.platform_id

            // Add superview emulation for downstream components
            return creators
                .filter((creator) => creator && (creator._id || creator.id || creator.profile?._id))
                .map((creator, index) => {
                    // Debug: log first few creators to see their actual structure
                    if (index < 2) {
                        console.log('Creator data structure:', creator)
                    }

                    // Determine the correct internal ID
                    // Priority: _id (standard) > id (common API) > profile._id (nested)
                    const internalId = creator._id || creator.id || creator.profile?._id

                    let primarySourceId = creator.source_ids?.[0]
                    let platformId = primarySourceId?.platform_id || 'unknown'
                    let creatorId = primarySourceId?.id

                    // If we have a searched platform, try to find that specific platform for this creator
                    if (searchedPlatform && creator.source_ids) {
                        const platformSource = creator.source_ids.find(
                            (source) => source.platform_id === searchedPlatform,
                        )
                        if (platformSource) {
                            platformId = searchedPlatform
                            creatorId = platformSource.id
                            primarySourceId = platformSource
                        }
                    }

                    // Ensure we have proper name values - check multiple possible field names
                    const displayName =
                        creator.display_name || creator.name || creator.username || creator.handle || null
                    const name = creator.name || creator.username || creator.handle || creator.display_name || null

                    return {
                        ...creator,
                        // Ensure the top-level object has an _id for components that expect it directly
                        _id: internalId,
                        superview: {
                            _id: creatorId || internalId,
                            platform_id: platformId,
                            normalized: {
                                _id: internalId,
                                display_name: displayName,
                                avatar_url:
                                    creator.avatar_url || creator.profile_image_url || creator.banner_image_url || null,
                                followers: creator.total_followers || creator.followers || 0,
                                platform_id: platformId,
                                name: name,
                                // Add additional fallback fields
                                username: creator.username || creator.handle || name,
                                handle: creator.handle || creator.username || name,
                            },
                        },
                    }
                })
        }

        const getAffinityData = (creator) => {
            const creatorId = creator._id
            if (loadingReasoning.value[creatorId]) {
                return 'loading'
            }
            return creatorAffinity.value[creatorId] || { reasoning: 'Reasoning unavailable', score: 0 }
        }

        // Enhanced error handling functions
        const createErrorMessage = (error, retryData = null) => {
            const errorData = error.response?.data?.error || {}

            return {
                id: `error_${Date.now()}`,
                role: 'assistant',
                type: 'error',
                content: errorData.message || error.message || 'An unexpected error occurred',
                errorType: errorData.type || 'internal_error',
                retryable: errorData.retryable || false,
                retryAfter: errorData.retryAfter,
                retryData,
                timestamp: Date.now(),
            }
        }

        const dismissError = (messageId) => {
            messages.value = messages.value.filter((msg) => msg.id !== messageId)
            // Clean up any countdown timers
            if (retryCountdowns.value[messageId]) {
                clearInterval(retryCountdowns.value[messageId])
                delete retryCountdowns.value[messageId]
            }
        }

        const retryLastRequest = async (errorMessage) => {
            if (!errorMessage.retryData || retryingMessage.value) return

            retryingMessage.value = errorMessage.id

            // Start the timer for retry
            startTimer()

            try {
                // Remove the error message from display
                dismissError(errorMessage.id)

                // Retry the original request
                await performChatRequest(errorMessage.retryData.messages, errorMessage.retryData.searchQuery)
            } catch (retryError) {
                console.error('Retry failed:', retryError)
                // Add a new error message for the failed retry
                const newErrorMessage = createErrorMessage(retryError, errorMessage.retryData)
                messages.value = [...messages.value, newErrorMessage]
            } finally {
                retryingMessage.value = null
                // Stop the timer
                stopTimer()
            }
        }

        const formatRetryCountdown = (message) => {
            if (!message.retryAfter) return ''

            const elapsed = (Date.now() - message.timestamp) / 1000
            const remaining = Math.max(0, message.retryAfter - elapsed)

            if (remaining <= 0) return 'Ready to retry'

            const minutes = Math.floor(remaining / 60)
            const seconds = Math.floor(remaining % 60)

            if (minutes > 0) {
                return `${minutes}m ${seconds}s`
            }
            return `${seconds}s`
        }

        const getRetryProgress = (message) => {
            if (!message.retryAfter) return 100

            const elapsed = (Date.now() - message.timestamp) / 1000
            const progress = (elapsed / message.retryAfter) * 100

            return Math.min(100, Math.max(0, progress))
        }

        // Thinking step collapse/expand functions
        const toggleCurrentThinking = () => {
            isCurrentThinkingExpanded.value = !isCurrentThinkingExpanded.value
        }

        const toggleThinkingSteps = (messageId) => {
            const collapsed = collapsedThinkingSteps.value
            if (collapsed.has(messageId)) {
                collapsed.delete(messageId)
            } else {
                collapsed.add(messageId)
            }
            collapsedThinkingSteps.value = new Set(collapsed)
        }

        const isThinkingExpanded = (messageId) => {
            return !collapsedThinkingSteps.value.has(messageId)
        }

        // Set up countdown timers for rate limit errors
        const setupRetryCountdown = (message) => {
            if (message.errorType === 'rate_limit_error' && message.retryAfter) {
                const intervalId = setInterval(() => {
                    const remaining = message.retryAfter - (Date.now() - message.timestamp) / 1000
                    if (remaining <= 0) {
                        clearInterval(intervalId)
                        delete retryCountdowns.value[message.id]
                    }
                }, 1000)

                retryCountdowns.value[message.id] = intervalId
            }
        }

        // Load user lists for the attachment dropdown
        const loadUserLists = async () => {
            isLoadingLists.value = true
            try {
                const { data } = await axios.get('/lists')
                // Combine user's own lists and shared lists
                userLists.value = [
                    ...(data.payload.myLists || []),
                    ...(data.payload.sharedLists || []),
                ]
            } catch (err) {
                console.error('Failed to load lists:', err)
                userLists.value = []
            } finally {
                isLoadingLists.value = false
            }
        }

        const handleListSelect = (list) => {
            const alreadySelected = selectedLists.value.some(l => l.id === list.id)
            if (!alreadySelected) {
                selectedLists.value.push({ id: list.id, name: list.name })
            }
            isWelcomeListDropdownOpen.value = false
            isConversationListDropdownOpen.value = false
        }

        const handleListRemove = (listId) => {
            selectedLists.value = selectedLists.value.filter(l => l.id !== listId)
        }

        const listActionIsDestructive = (listAction) => {
            return listAction.action === 'delete' || (listAction.action === 'update' && listAction.operation === 'remove')
        }

        const listActionConfirmLabel = (listAction) => {
            if (listAction.action === 'delete') return 'Delete List'
            if (listAction.action === 'update') return listAction.operation === 'remove' ? 'Remove Profiles' : 'Add Profiles'
            return 'Create List'
        }

        const listActionLoadingText = (listAction) => {
            if (listAction.action === 'delete') return 'Deleting list...'
            if (listAction.action === 'update') return listAction.operation === 'remove' ? 'Removing profiles...' : 'Adding profiles...'
            return 'Creating list...'
        }

        const listActionDoneText = (listAction) => {
            if (listAction.action === 'delete') return 'List deleted!'
            if (listAction.action === 'update') return listAction.operation === 'remove' ? 'Profiles removed!' : 'Profiles added!'
            return 'List created!'
        }

        const confirmListAction = async (message) => {
            listActionEditing.value = null
            const action = JSON.parse(JSON.stringify(message.listAction))
            listActionState.value = { ...listActionState.value, [message.id]: 'loading' }
            try {
                if (action.action === 'delete') {
                    await axios.delete(`/lists/${action.list_id}`)
                } else if (action.action === 'update') {
                    if (action.operation === 'add' && action.creators?.length) {
                        console.log(`[ListAction] Adding ${action.creators.length} creators to list ${action.list_id}:`, action.creators)
                        await axios.post(`/lists/${action.list_id}/creators`, {
                            creators: action.creators,
                        })
                    } else if (action.operation === 'remove' && action.creators?.length) {
                        console.log(`[ListAction] Removing ${action.creators.length} creators from list ${action.list_id}:`, action.creators)
                        await axios.delete(`/lists/${action.list_id}/creators`, {
                            data: { creators: action.creators },
                        })
                    }
                } else {
                    const operatorId = store.state.self.operator.id
                    const { data } = await axios.post('/lists', {
                        list: { name: action.suggested_name, operator_id: operatorId },
                    })
                    const newListId = data.payload.list.id
                    if (newListId && action.creators?.length) {
                        console.log(`[ListAction] Adding ${action.creators.length} creators to list ${newListId}:`, action.creators)
                        await axios.post(`/lists/${newListId}/creators`, {
                            creators: action.creators,
                        })
                    }
                }
                loadUserLists() // non-blocking — refresh lists in background
                const doneText = listActionDoneText(action)
                toast({ title: doneText, description: `"${action.list_name || action.suggested_name}"` })
                const idx = messages.value.findIndex(m => m.id === message.id)
                message.listAction = null
                messages.value.splice(idx + 1, 0, {
                    id: `action-result-${Date.now()}`,
                    role: 'action_result',
                    content: doneText,
                    cancelled: false,
                })
                const { [message.id]: _, ...rest } = listActionState.value
                listActionState.value = rest
            } catch (err) {
                console.error(`Failed to ${action.action} list from AI action:`, err)
                const { [message.id]: _, ...rest } = listActionState.value
                listActionState.value = rest
            }
        }

        const cancelListAction = (message) => {
            listActionEditing.value = null
            const idx = messages.value.findIndex(m => m.id === message.id)
            message.listAction = null
            messages.value.splice(idx + 1, 0, {
                id: `action-result-${Date.now()}`,
                role: 'action_result',
                content: 'Operation cancelled',
                cancelled: true,
            })
        }

        const toggleCreatorReasoning = (creator) => {
            creator.expanded = !creator.expanded
        }

        // Conversation management functions
        const loadConversations = async ({ append = false } = {}) => {
            if (append) {
                if (isLoadingMoreConversations.value || !hasMoreConversations.value) return
                isLoadingMoreConversations.value = true
            } else {
                isLoadingConversations.value = true
                conversationSkip.value = 0
                hasMoreConversations.value = true
            }

            try {
                const response = await axios.get('/ai/conversations', {
                    params: {
                        limit: conversationPageLimit,
                        skip: append ? conversationSkip.value : 0,
                    },
                })

                if (response.data.success) {
                    const fetchedConversations = response.data.payload.conversations || []
                    const mappedConversations = fetchedConversations.map((conversation) => {
                        if (conversation.title === 'New Conversation') {
                            const cachedTitle = getCachedConversationTitle(conversation._id)
                            if (cachedTitle) {
                                persistConversationTitle(conversation._id, cachedTitle)
                                return { ...conversation, title: cachedTitle }
                            }
                        }
                        return conversation
                    })

                    if (append) {
                        const existingIds = new Set(conversations.value.map((conversation) => conversation._id))
                        const nextConversations = mappedConversations.filter(
                            (conversation) => !existingIds.has(conversation._id),
                        )
                        conversations.value = [...conversations.value, ...nextConversations]
                    } else {
                        conversations.value = mappedConversations
                    }

                    conversationSkip.value = (append ? conversationSkip.value : 0) + fetchedConversations.length
                    hasMoreConversations.value = fetchedConversations.length === conversationPageLimit
                }
            } catch (error) {
                console.error('Failed to load conversations:', error)
            } finally {
                if (append) {
                    isLoadingMoreConversations.value = false
                } else {
                    isLoadingConversations.value = false
                }
            }
        }

        const handleLoadMoreConversations = async () => {
            await loadConversations({ append: true })
        }

        const loadConversation = async (conversationId) => {
            try {
                const response = await axios.get(`/ai/conversations/${conversationId}`)
                if (response.data.success) {
                    const conversation = response.data.payload.conversation
                    currentConversationId.value = conversationId
                    const filteredMessages = (conversation.messages || [])
                        .filter((msg) => msg && msg.id && msg.role && msg.content !== undefined)
                    const validMessages = filteredMessages
                        .map((msg) => {
                            if (msg.role === 'assistant') {
                                return {
                                    ...msg,
                                    fromHistory: true,
                                    content: sanitizeAssistantContent(msg.content),
                                    creators: msg.creators || [],
                                    rejectedCreators: msg.rejectedCreators || [],
                                    searchContext: msg.searchContext || {},
                                    filteringStats: msg.filteringStats || {},
                                    evaluationTable: msg.evaluation_table
                                        ? msg.evaluation_table.map(entry => {
                                            let reasoning = entry.reasoning
                                            if (typeof reasoning === 'string') {
                                                try { reasoning = JSON.parse(reasoning) } catch {}
                                            }
                                            if (reasoning && typeof reasoning === 'object') {
                                                reasoning = reasoning.recommendation || null
                                            }
                                            return { ...entry, reasoning, expanded: false }
                                        })
                                        : null,
                                }
                            }
                            if (msg.role === 'user') {
                                const rawListIds = msg.list_ids ?? msg.listIds
                                const listIds = Array.isArray(rawListIds) ? rawListIds : []
                                return {
                                    ...msg,
                                    attachedLists: listIds
                                        .map(id => {
                                            const list = userLists.value.find(l => l.id === id)
                                            return list ? { id: list.id, name: list.name } : null
                                        })
                                        .filter(Boolean),
                                }
                            }
                            return msg
                        })
                    setConversationMessages(conversationId, validMessages)
                }
            } catch (error) {
                console.error('Failed to load conversation:', error)
            }
        }

        const handleSelectConversation = async (conversationId) => {
            if (conversationId === currentConversationId.value) return

            evaluatingCreators.value = []
            isLoadingPreviousConversation.value = true
            isAwaitingClarification.value = false
            currentConversationId.value = conversationId
            selectedLists.value = selectedListsByConversationId.value[conversationId] || []

            const cachedMessages = getConversationMessages(conversationId)
            if (cachedMessages.length > 0) {
                messages.value = cachedMessages
            } else {
                await loadConversation(conversationId)
            }

            // Update URL
            router.push({ name: 'ai-chat-conversation', params: { conversationId } })

            isLoadingPreviousConversation.value = false

            // Scroll to bottom and focus input after messages render
            nextTick(() => {
                scrollToBottom(false)
                const inputElement = autoGrowConversation.value
                if (inputElement) {
                    inputElement.focus()
                }
            })
        }

        const handleNewConversation = () => {
            messages.value = []
            evaluatingCreators.value = []
            currentConversationId.value = null
            isAwaitingClarification.value = false
            // Navigate to new conversation (no ID in URL)
            router.push({ name: 'ai-chat' })

            // Focus appropriate input box after navigation
            nextTick(() => {
                // Use welcome input if no messages, conversation input if there are messages
                const inputElement = messages.value.length === 0 ? autoGrowWelcome.value : autoGrowConversation.value
                if (inputElement) {
                    inputElement.focus()
                }
            })
        }

        const handleRenameConversation = async (conversationId, newTitle) => {
            console.log(`🏷️  Renaming conversation ${conversationId} to "${newTitle}"`)
            try {
                const response = await axios.put(`/ai/conversations/${conversationId}/title`, {
                    title: newTitle,
                })
                console.log('Rename response:', response.data)
                if (response.data.success) {
                    // Update the conversation in the list
                    const conversation = conversations.value.find((c) => c._id === conversationId)
                    if (conversation) {
                        conversation.title = newTitle
                        console.log(`✅ Updated conversation title in UI: "${newTitle}"`)
                    } else {
                        console.warn(`⚠️  Conversation ${conversationId} not found in local list`)
                    }
                } else {
                    console.error('Rename failed:', response.data)
                }
            } catch (error) {
                console.error('Failed to rename conversation:', error)
                console.error('Error details:', error.response?.data)
            }
        }

        const handleDeleteConversation = async (conversationId) => {
            try {
                const response = await axios.delete(`/ai/conversations/${conversationId}`)
                if (response.data.success) {
                    // Remove from list
                    conversations.value = conversations.value.filter((c) => c._id !== conversationId)
                    // If this was the current conversation, start a new one
                    if (currentConversationId.value === conversationId) {
                        handleNewConversation()
                    }
                }
            } catch (error) {
                console.error('Failed to delete conversation:', error)
            }
        }

        // Creator management functions
        const handleAddToList = async ({ creators, list }) => {
            console.log({ creators })

            try {
                const response = await axios.post(`/lists/${list.id}/creators`, {
                    creators: _.map(creators, ({ superview }) => {
                        return {
                            platform_id: superview.platform_id,
                            platform_user_id: superview._id,
                            platform_user_name: superview.normalized.name,
                        }
                    }),
                })

                if (response.data.success) {
                    // Show success notification
                    // You can customize this based on your notification system
                    console.log(`Added ${creators.length} creators to list: ${list.name}`)
                }
            } catch (error) {
                console.error('Failed to add creators to list:', error)
            }
        }

        const handleAddToCampaign = async ({ creators, supercampaignId, campaignId, type }) => {
            try {
                if (type === 'add-selected') {
                    const response = await axios.post(
                        `/supercampaigns/${supercampaignId}/campaigns/${campaignId}/participants`,
                        {
                            creators,
                        },
                    )
                    if (response.data.success) {
                        // Show success notification
                        console.log(`Added ${creators.length} creators to campaign`)
                    }
                }
            } catch (error) {
                console.error('Failed to add creators to campaign:', error)
            }
        }

        // Streaming chat request with real-time thinking display
        const performStreamingChatRequest = async (requestMessages, searchQuery, conversationKey) => {
            console.log('🔄 performStreamingChatRequest started with:', { requestMessages, searchQuery })
            const startTime = moment()
            currentThinkingSteps.value = []
            evaluatingCreators.value = []
            isStreaming.value = true
            isCurrentThinkingExpanded.value = false // Start collapsed

            // Create AbortController for this request
            const controller = new AbortController()
            currentStreamController.value = controller

            try {
                // Use fetch for SSE with axios base URL and headers
                const axiosConfig = axios.defaults
                const baseURL = axiosConfig.baseURL || ''

                const endpoint = `${baseURL}/ai/chat/stream`
                const currentListIds = requestMessages
                    .filter(m => m.role === 'user' && m.list_ids?.length)
                    .flatMap(m => m.list_ids)

                // Strip frontend-only fields before sending — backend only needs role + content
                // (and list_ids for user messages so it knows which lists are attached)
                const apiMessages = requestMessages.map(m => {
                    if (m.role === 'user') {
                        return { role: m.role, content: m.content, list_ids: m.list_ids || [] }
                    }
                    return { role: m.role, content: m.content }
                })

                const requestBody = {
                    messages: apiMessages,
                    conversation_id: currentConversationId.value,
                    user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    list_ids: [...new Set(currentListIds)],
                }

                console.log('🌐 Making fetch request to:', endpoint)
                console.log('📝 Request body:', {
                    messagesCount: requestMessages.length,
                    conversation_id: requestBody.conversation_id,
                    user_timezone: requestBody.user_timezone,
                    isRetry: !!currentConversationId.value
                })

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...axiosConfig.headers,
                    },
                    body: JSON.stringify(requestBody),
                    signal: controller.signal,
                })

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                }

                // Handle the stream response
                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let buffer = '' // Buffer for partial data

                const readStream = async () => {
                    const { done, value } = await reader.read()

                    if (done) {
                        isStreaming.value = false
                        return
                    }

                    // Add new chunk to buffer
                    buffer += decoder.decode(value, { stream: true })

                    // Process complete lines
                    const lines = buffer.split('\n')
                    buffer = lines.pop() // Keep incomplete line in buffer

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const dataStr = line.slice(6).trim() // Remove 'data: ' prefix

                            if (dataStr === '[DONE]') {
                                isStreaming.value = false
                                return
                            }

                            if (dataStr) {
                                // Only parse non-empty data
                                try {
                                    const data = JSON.parse(dataStr)
                                    await maybeSetConversationId(
                                        data?.payload?.conversation_id || data?.conversation_id || data?.data?.conversation_id,
                                        conversationKey
                                    )

                                    if (data.type === 'thinking') {
                                        // Add thinking step to real-time display
                                        currentThinkingSteps.value.push({
                                            step: data.step,
                                            data: data.data,
                                            timestamp: data.timestamp,
                                        })
                                    } else if (data.type === 'creator_evaluated') {
                                        // Live creator evaluation update for list refinement.
                                        // The backend sends 'pending' first (before LLM call), then
                                        // 'keep'/'remove' for the same creatorId when done — update in-place.
                                        const existingIndex = evaluatingCreators.value.findIndex(c => c.id === data.creatorId)
                                        if (existingIndex >= 0) {
                                            evaluatingCreators.value[existingIndex] = {
                                                ...evaluatingCreators.value[existingIndex],
                                                decision: data.decision,
                                                reasoning: data.reasoning || evaluatingCreators.value[existingIndex].reasoning,
                                            }
                                        } else {
                                            evaluatingCreators.value.push({
                                                id: data.creatorId,
                                                name: data.name,
                                                decision: data.decision,
                                                reasoning: data.reasoning,
                                                expanded: false,
                                            })
                                        }
                                        // Yield to Vue's renderer so each row update is visible
                                        await nextTick()
                                    } else if (data.type === 'clarification_request') {
                                        // Handle AI clarification request
                                        console.log('📋 AI is asking for clarification:', data.questions)

                                        // Stop the thinking display
                                        isStreaming.value = false
                                        isAwaitingClarification.value = true

                                        // Create clarification message
                                        const clarificationMessage = {
                                            id: `clarification_${Date.now()}`,
                                            role: 'assistant',
                                            content: 'I need some clarification to help you find the right creators.',
                                            clarification_questions: data.questions,
                                            assumptions: data.assumptions,
                                            timestamp: data.timestamp,
                                            type: 'clarification'
                                        }

                                        messages.value = [...messages.value, clarificationMessage]
                                        return // Stop processing stream

                                    } else if (data.type === 'deconstruction') {
                                        // Add deconstruction step to real-time display
                                        currentThinkingSteps.value.push({
                                            step: 'Query Analysis Complete',
                                            data: {
                                                type: 'deconstruction',
                                                details: data.data,
                                                research_summaries: data.research_summaries || [],
                                            },
                                            timestamp: data.timestamp,
                                        })

                                        // Update conversation title if we have an AI-generated title
                                        if (data.data.success && data.data.deconstruction?.conversation_title) {
                                            const aiTitle = data.data.deconstruction.conversation_title
                                            const tempId = window.tempConversationId || currentConversationId.value
                                            await applyConversationTitle(tempId, aiTitle)
                                        }
                                    } else if (data.type === 'plan') {
                                        // Add plan step to real-time display
                                        currentThinkingSteps.value.push({
                                            step: 'Search Strategy Formulated',
                                            data: {
                                                type: 'plan',
                                                details: data.data,
                                            },
                                            timestamp: data.timestamp,
                                        })
                                    } else if (data.type === 'result') {
                                        console.log('✅ Prompt complete', data.payload?.conversation_id)
                                        // Handle final result
                                        const endTime = moment()
                                        const duration = endTime.diff(startTime)
                                        let targetConversationKey = conversationKey
                                        let conversationMessages = getConversationMessages(conversationKey)
                                        const conversationMessageCount = conversationMessages.length

                                        // Update conversation ID if new
                                        if (data.payload.conversation_id) {
                                            const realConversationId = data.payload.conversation_id
                                            targetConversationKey = realConversationId

                                            // Find and update the temporary conversation
                                            const tempId = window.tempConversationId
                                            const tempConversationIndex = conversations.value.findIndex(
                                                (c) => c._id === tempId && c.isTemporary,
                                            )

                                            if (tempConversationIndex !== -1) {
                                                // Update the temporary conversation with real ID and remove temporary flag
                                                const tempConversation = conversations.value[tempConversationIndex]
                                                conversations.value[tempConversationIndex] = {
                                                    ...tempConversation,
                                                    _id: realConversationId,
                                                    isTemporary: false,
                                                    message_count: conversationMessageCount,
                                                    updated_at: new Date().toISOString(),
                                                    last_message_at: new Date().toISOString(),
                                                }
                                                migrateConversationMessages(tempId, realConversationId)
                                                inFlightConversationKey.value = realConversationId
                                                conversationMessages = getConversationMessages(realConversationId)

                                                // Clean up
                                                if (window.tempConversationId) {
                                                    delete window.tempConversationId
                                                }
                                                if (window.pendingConversationTitle) {
                                                    delete window.pendingConversationTitle
                                                }
                                            } else {
                                                const existingConversationIndex = conversations.value.findIndex(
                                                    (c) => c._id === realConversationId,
                                                )

                                                if (existingConversationIndex !== -1) {
                                                    conversations.value[existingConversationIndex] = {
                                                        ...conversations.value[existingConversationIndex],
                                                        message_count: conversationMessageCount,
                                                        updated_at: new Date().toISOString(),
                                                        last_message_at: new Date().toISOString(),
                                                    }
                                                    inFlightConversationKey.value = realConversationId
                                                    conversationMessages = getConversationMessages(realConversationId)
                                                } else {
                                                    // Fallback: create new conversation if temp one wasn't found
                                                    const firstUserMessage = conversationMessages.find((m) => m.role === 'user')
                                                    const title =
                                                        window.pendingConversationTitle ||
                                                        firstUserMessage?.content ||
                                                        'New Conversation'

                                                    const newConversation = {
                                                        _id: realConversationId,
                                                        title: title,
                                                        created_at: new Date().toISOString(),
                                                        updated_at: new Date().toISOString(),
                                                        last_message_at: new Date().toISOString(),
                                                        message_count: conversationMessageCount,
                                                        messages: conversationMessages.slice(0, 1),
                                                    }

                                                    conversations.value.unshift(newConversation)
                                                    inFlightConversationKey.value = realConversationId
                                                    conversationMessages = getConversationMessages(realConversationId)
                                                }

                                                // Clean up
                                                if (window.tempConversationId) {
                                                    delete window.tempConversationId
                                                }
                                                if (window.pendingConversationTitle) {
                                                    delete window.pendingConversationTitle
                                                }
                                            }

                                            // Update current conversation ID and URL
                                            currentConversationId.value = realConversationId
                                            const routeName = route?.name?.toString() || ''
                                            const isAiChatRoute = routeName.startsWith('ai-chat')
                                            if (isAiChatRoute && getActiveConversationKey() === conversationKey) {
                                                router.push({
                                                    name: 'ai-chat-conversation',
                                                    params: { conversationId: realConversationId },
                                                })
                                            }

                                            if (window.pendingConversationTitle) {
                                                await persistConversationTitle(
                                                    realConversationId,
                                                    window.pendingConversationTitle,
                                                )
                                                delete window.pendingConversationTitle
                                            }
                                        }

                                        // Create assistant message
                                        const assistantMessage = {
                                            id: data.payload.message_id || `assistant_${Date.now()}`,
                                            role: 'assistant',
                                            content: sanitizeAssistantContent(data.payload.text),
                                            hasCreatorResults: data.payload.hasCreatorResults,
                                            creator_sample: data.payload.creators || [],
                                            creator_count: data.payload.totalCreators,
                                            total_followers: data.payload.totalFollowers,
                                            rejectedCreators: data.payload.rejectedCreators || [],
                                            searchContext: data.payload.searchContext || {},
                                            filteringStats: data.payload.filteringStats || {},
                                            toolResults: data.payload.toolResults || [],
                                            searchQuery: searchQuery,
                                            duration: duration,
                                            thinkingSteps: [...currentThinkingSteps.value], // Preserve thinking steps
                                            deconstruction: data.payload.deconstruction,
                                            searchPlan: data.payload.searchPlan,
                                            listAction: data.payload.listAction || null,
                                            evaluationTable: evaluatingCreators.value.length > 0
                                                ? evaluatingCreators.value.map(c => ({ ...c }))
                                                : null,
                                        }

                                        const currentMessages = getConversationMessages(targetConversationKey)
                                        setConversationMessages(targetConversationKey, [
                                            ...currentMessages,
                                            assistantMessage,
                                        ])
                                        currentThinkingSteps.value = [] // Clear for next request
                                        evaluatingCreators.value = []   // Frozen onto message above
                                        isStreaming.value = false
                                        isAwaitingClarification.value = false

                                        if (
                                            !window.pendingConversationTitle &&
                                            data.payload?.deconstruction?.conversation_title
                                        ) {
                                            await applyConversationTitle(
                                                targetConversationKey,
                                                data.payload.deconstruction.conversation_title,
                                            )
                                        }

                                        // Auto-collapse thinking steps after completion
                                        collapsedThinkingSteps.value.add(assistantMessage.id)

                                        inFlightConversationKey.value = null
                                        return data
                                    } else if (data.type === 'error') {
                                        isStreaming.value = false
                                        currentThinkingSteps.value = []
                                        evaluatingCreators.value = []
                                        inFlightConversationKey.value = null
                                        throw new Error(data.error)
                                    }
                                } catch (parseError) {
                                    console.warn('Failed to parse SSE data:', parseError.message, 'Data:', dataStr)
                                }
                            }
                        }
                    }

                    // Continue reading if not done
                    await readStream()
                }

                // Start reading the stream
                await readStream()

                // Trigger ai search completed event
                trackEvent(AnalyticalEvents.AIAgentSearchCompleted, {
                    conversation_id: currentConversationId.value
                })

            } catch (error) {
                // Clean up stream controller
                currentStreamController.value = null
                isStreaming.value = false
                currentThinkingSteps.value = []
                inFlightConversationKey.value = null

                // Don't throw error if request was aborted (user cancelled/edited)
                if (error.name === 'AbortError') {
                    console.log('🛑 Chat stream cancelled by user')
                    return
                }

                console.error('❌ Chat stream error:', error)
                throw error
            } finally {
                // Always clean up the controller reference
                if (currentStreamController.value === controller) {
                    currentStreamController.value = null
                }
                inFlightConversationKey.value = null
            }
        }

        watch(messages, (newMessages) => {
            const activeKey = getActiveConversationKey()
            if (activeKey) {
                messagesByConversationKey.value = {
                    ...messagesByConversationKey.value,
                    [activeKey]: newMessages,
                }
            }
            const lastMessage = newMessages[newMessages.length - 1]
            if (lastMessage && lastMessage.role === 'assistant' && lastMessage.hasCreatorResults) {
                const creators = lastMessage.creators || []
                if (!creators || creators.length === 0) return

                const loadingState = {}
                creators.forEach((creator) => {
                    loadingState[creator._id] = true
                })
                loadingReasoning.value = { ...loadingReasoning.value, ...loadingState }

                // creators.forEach(async (creator) => {
                //     try {
                //         const affinityData = await fetchCreatorReasoning(creator._id, lastMessage.searchQuery)

                //         // Update immediately as each request completes
                //         creatorAffinity.value = {
                //             ...creatorAffinity.value,
                //             [creator._id]: affinityData
                //         }
                //         loadingReasoning.value = {
                //             ...loadingReasoning.value,
                //             [creator._id]: false
                //         }
                //     } catch (error) {
                //         console.error(`❌ Error fetching reasoning for creator ${creator._id}:`, error)
                //         // Set error state for this specific creator
                //         creatorAffinity.value = {
                //             ...creatorAffinity.value,
                //             [creator._id]: { reasoning: 'Error generating reasoning', score: 0 }
                //         }
                //         loadingReasoning.value = {
                //             ...loadingReasoning.value,
                //             [creator._id]: false
                //         }
                //     }
                // })
            }

            // Set up countdown timers for new error messages
            if (lastMessage && lastMessage.type === 'error') {
                setupRetryCountdown(lastMessage)
            }
        })

        // Message action functions
        const copyMessageToClipboard = async (message) => {
            try {
                await navigator.clipboard.writeText(message.content)
                // Could add a toast notification here if desired
            } catch (error) {
                console.error('Failed to copy message:', error)
            }
        }

        const retryFromMessage = async (message) => {
            console.log(`🔄 Retrying from message in conversation: ${currentConversationId.value}`)

            // Cancel current request if one is active
            if (currentStreamController.value) {
                currentStreamController.value.abort()
                currentStreamController.value = null
                isStreaming.value = false
                currentThinkingSteps.value = []
                stopTimer()
            }

            // Find the index of this message in the conversation
            const messageIndex = messages.value.findIndex((m) => m.id === message.id)
            if (messageIndex === -1) return

            // Truncate conversation up to and including this message
            const truncatedMessages = messages.value.slice(0, messageIndex + 1)
            const conversationKey = getActiveConversationKey()
            setConversationMessages(conversationKey, truncatedMessages)

            // Update the conversation in the sidebar to reflect the new message count
            if (currentConversationId.value) {
                const conversationIndex = conversations.value.findIndex((c) => c._id === currentConversationId.value)
                if (conversationIndex !== -1) {
                    conversations.value[conversationIndex].message_count = truncatedMessages.length
                    conversations.value[conversationIndex].updated_at = new Date().toISOString()
                    conversations.value[conversationIndex].last_message_at = new Date().toISOString()
                }
            }

            isLoading.value = true
            error.value = null
            inFlightConversationKey.value = conversationKey

            // Start the timer
            startTimer()

            try {
                await performStreamingChatRequest(truncatedMessages, message.content, conversationKey)
            } catch (err) {
                console.error('❌ Retry error:', err)

                const errorMessage = createErrorMessage(err, {
                    messages: truncatedMessages,
                    searchQuery: message.content,
                })

                const currentMessages = getConversationMessages(conversationKey)
                setConversationMessages(conversationKey, [...currentMessages, errorMessage])
                error.value = err.response?.data?.message || err.message
            } finally {
                isLoading.value = false
                stopTimer()
            }
        }

        const startEditingMessage = (message) => {
            // If there's an active stream request, cancel it
            if (currentStreamController.value) {
                currentStreamController.value.abort()
                currentStreamController.value = null
                isLoading.value = false
                isStreaming.value = false
                currentThinkingSteps.value = []
                stopTimer()
            }

            editingMessageId.value = message.id
            editingContent.value = message.content
        }

        const cancelEditingMessage = () => {
            editingMessageId.value = null
            editingContent.value = ''
        }

        const saveEditedMessage = async () => {
            if (!editingContent.value.trim()) return

            // Cancel current request if one is active
            if (currentStreamController.value) {
                currentStreamController.value.abort()
                currentStreamController.value = null
                isStreaming.value = false
                currentThinkingSteps.value = []
                stopTimer()
            }

            const messageId = editingMessageId.value
            const messageIndex = messages.value.findIndex((m) => m.id === messageId)
            if (messageIndex === -1) return

            // Update the message content
            messages.value[messageIndex].content = editingContent.value.trim()

            // Truncate conversation up to and including this message
            const truncatedMessages = messages.value.slice(0, messageIndex + 1)
            const conversationKey = getActiveConversationKey()
            setConversationMessages(conversationKey, truncatedMessages)

            // Update the conversation in the sidebar to reflect the new message count
            if (currentConversationId.value) {
                const conversationIndex = conversations.value.findIndex((c) => c._id === currentConversationId.value)
                if (conversationIndex !== -1) {
                    conversations.value[conversationIndex].message_count = truncatedMessages.length
                    conversations.value[conversationIndex].updated_at = new Date().toISOString()
                    conversations.value[conversationIndex].last_message_at = new Date().toISOString()
                }
            }

            // Clear editing state
            editingMessageId.value = null
            editingContent.value = ''

            isLoading.value = true
            error.value = null
            inFlightConversationKey.value = conversationKey

            // Start the timer
            startTimer()

            try {
                await performStreamingChatRequest(truncatedMessages, truncatedMessages[messageIndex].content, conversationKey)
            } catch (err) {
                console.error('❌ Edit retry error:', err)

                const errorMessage = createErrorMessage(err, {
                    messages: truncatedMessages,
                    searchQuery: truncatedMessages[messageIndex].content,
                })

                const currentMessages = getConversationMessages(conversationKey)
                setConversationMessages(conversationKey, [...currentMessages, errorMessage])
                error.value = err.response?.data?.message || err.message
            } finally {
                isLoading.value = false
                stopTimer()
            }
        }

        const giveFeedback = async (message, value) => {
            if (!currentConversationId.value || !message?.id) {
                console.error('Missing conversation ID or message ID:', {
                    conversationId: currentConversationId.value,
                    messageId: message?.id
                })
                return
            }
            if (feedbackSubmitting.value) return

            console.log('Submitting feedback:', {
                conversationId: currentConversationId.value,
                messageId: message.id,
                feedback: value,
                url: `/ai/conversations/${currentConversationId.value}/messages/${message.id}/feedback`
            })

            try {
                feedbackSubmitting.value = true
                await axios.put(`/ai/conversations/${currentConversationId.value}/messages/${message.id}/feedback`, {
                    feedback: value,
                })
                const idx = messages.value.findIndex((m) => m.id === message.id)
                if (idx !== -1) {
                    messages.value[idx] = { ...messages.value[idx], feedback: value }
                }
                console.log('Feedback submitted successfully')
            } catch (err) {
                console.error('Failed to submit feedback:', err)
                console.error('Error details:', {
                    status: err.response?.status,
                    data: err.response?.data,
                    message: err.message
                })
            } finally {
                feedbackSubmitting.value = false
            }
        }

        // Enhanced chat functionality with proper error handling
        const handleSubmit = async () => {
            console.log('🚀 handleSubmit called, input:', input.value.trim(), 'isLoading:', isLoading.value)
            if (!input.value.trim() || isLoading.value) return

            const userMessage = {
                id: `user_${Date.now()}`,
                role: 'user',
                content: input.value.trim(),
                list_ids: selectedLists.value.map(l => l.id),
                attachedLists: selectedLists.value.map(l => ({ id: l.id, name: l.name })),
            }

            // Potentially add something to distinguish between freeform vs preset prompts in the future
            trackEvent(AnalyticalEvents.AIAgentPrompted, {
                feature_name: 'ai_chat',
                prompt_length: userMessage.content.length,
            })

            const newMessages = [...messages.value, userMessage]
            const searchQuery = input.value.trim()
            input.value = ''
            selectedLists.value = []

            // Reset textarea height to default after clearing input
            const currentTextarea = messages.value.length === 1 ? autoGrowWelcome.value : autoGrowConversation.value
            if (currentTextarea) {
                // Use nextTick to ensure the input value has been cleared before resizing
                nextTick(() => {
                    const el = currentTextarea
                    const parentEl = textareaParent.value
                    if (el && parentEl) {
                        const max = parentEl.clientHeight / 2
                        el.style.height = 'auto'
                        el.style.height = Math.min(el.scrollHeight, max) + 'px'
                    }
                })
            }

            isLoading.value = true
            error.value = null

            // If this is the first message (new conversation), create or update a temporary entry
            const isNewConversation = !currentConversationId.value
            let conversationKey = currentConversationId.value || window.tempConversationId
            if (isNewConversation) {
                const tempId = window.tempConversationId
                const tempConversationIndex = tempId
                    ? conversations.value.findIndex((c) => c._id === tempId && c.isTemporary)
                    : -1

                if (tempConversationIndex !== -1) {
                    conversations.value[tempConversationIndex] = {
                        ...conversations.value[tempConversationIndex],
                        message_count: newMessages.length,
                        updated_at: new Date().toISOString(),
                        last_message_at: new Date().toISOString(),
                    }
                    conversationKey = tempId
                } else {
                    const tempTitle = searchQuery.length <= 50 ? searchQuery : searchQuery.substring(0, 47) + '...'
                    const tempConversation = {
                        _id: 'temp_' + Date.now(), // Temporary ID
                        title: tempTitle,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        last_message_at: new Date().toISOString(),
                        message_count: 1,
                        messages: [userMessage],
                        isTemporary: true, // Mark as temporary
                    }

                    // Add to conversations list but DON'T set currentConversationId yet
                    // The backend needs null for new conversations
                    conversations.value.unshift(tempConversation)
                    // Store temp ID for UI updates, but keep currentConversationId as null for backend
                    window.tempConversationId = tempConversation._id
                    conversationKey = tempConversation._id
                }
            }

            if (conversationKey) {
                setConversationMessages(conversationKey, newMessages)
            } else {
                messages.value = newMessages
            }

            inFlightConversationKey.value = conversationKey

            // Start the timer
            startTimer()

            try {
                console.log('📞 About to call performStreamingChatRequest with:', { newMessages, searchQuery })
                await performStreamingChatRequest(newMessages, searchQuery, conversationKey)
            } catch (err) {
                console.error('❌ Chat error:', err)

                // Create detailed error message with retry capability
                const errorMessage = createErrorMessage(err, {
                    messages: newMessages,
                    searchQuery: searchQuery,
                })

                messages.value = [...messages.value, errorMessage]
                error.value = err.response?.data?.message || err.message
            } finally {
                isLoading.value = false
                // Stop the timer
                stopTimer()
            }
        }

        const handleSuggestedQuery = (query) => {
            input.value = query
        }

        const handleKeyPress = (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
            }
        }

        const tableColumns = ref([
            {
                id: 'creator',
                key: 'display_name',
                header: {
                    label: 'Creator',
                    is_sortable: false,
                },
                row: {
                    slot: 'creator',
                    slotTransformer: (item) => ({
                        id: item.source_ids?.[0]?.id || item._id,
                        platform_id: item.source_ids?.[0]?.platform_id || 'unknown',
                        name: item.name || item.display_name,
                        display_name: item.display_name || item.name,
                        logo: item.banner_image_url || item.avatar_url,
                    }),
                },
                is_exportable: true,
                exportTransformer: (item) => ({
                    Name: item.display_name || item.name || '',
                    'Platform ID': item.source_ids?.[0]?.id || item._id || '',
                }),
                group: 'basic',
            },
            {
                id: 'platforms',
                key: 'source_ids',
                header: {
                    label: 'Platforms',
                    is_sortable: false,
                },
                row: {
                    slot: 'platforms',
                },
                is_exportable: true,
                exportTransformer: (item) => {
                    const platforms = (item.source_ids || []).map(s => s.platform_id).join(', ')
                    return { Platforms: platforms }
                },
                group: 'basic',
            },
            {
                id: 'audience',
                key: 'total_followers',
                header: {
                    label: 'Total Followers',
                    is_sortable: false,
                },
                row: {
                    formatter: ({ item }) => {
                        return item.total_followers ? formatNumber(item.total_followers) : 'N/A'
                    },
                },
                is_exportable: true,
                exportTransformer: (item) => ({
                    Followers: item.total_followers || 0,
                }),
                group: 'basic',
            },
            {
                id: 'location',
                key: 'residence.country_code',
                header: {
                    label: 'Location',
                    is_sortable: false,
                },
                row: {
                    formatter: ({ item }) => {
                        return item?.residence?.country_code
                    },
                },
                is_exportable: true,
                exportTransformer: (item) => ({
                    Location: item?.residence?.country_code || '',
                }),
                group: 'basic',
            },
            {
                id: 'affinity',
                key: 'reasoning.relevance_score',
                header: {
                    label: 'Affinity',
                    is_sortable: false,
                },
                row: {
                    slot: 'affinity',
                },
                is_exportable: true,
                exportTransformer: (item) => ({
                    'Affinity Score': getRelevanceScore(item) || 0,
                    'Recommendation': getReasoningData(item)?.recommendation || '',
                }),
                group: 'basic',
            },
        ])

        const enabledColumns = ref(_.map(tableColumns.value, 'id'))

        const columnGroups = [
            { id: 'basic', label: 'Basic', platforms: ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
            // { id: 'metrics', label: 'Metrics', platforms : ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
            // { id: 'audience', label: 'Audience', platforms : ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
            // { id: 'games', label: 'Games', platforms : ['youtube', 'twitch'] },
        ]

        const availableColumnGroups = ref(columnGroups)
        // const availableColumnGroups = computed(() => {
        //     return _.filter(columnGroups, group => { return group?.platforms?.includes(this.platformId) })
        // })

        const onEnabledColumnChange = (newEnabledColumns) => {
            enabledColumns.value = [...newEnabledColumns]

            store.commit('preferences:set', {
                key: 'tables.discovery.ai_chat.columns',
                value: enabledColumns.value,
            })
        }

        const onExportCreators = () => {
            const message = selectedMessageForModal.value
            if (!message) return

            const fileName = `AI Chat - ${message.content?.substring(0, 30) || 'creators'} - ${activeTab.value} creators`

            store.commit('modal:setData', {
                modal_id: 'export_table',
                data: {
                    is_open: true,
                    file_name: fileName,
                    columns: {
                        all: tableColumns.value,
                        enabled: tableColumns.value.filter(col => enabledColumns.value.includes(col.id)),
                    },
                    rowCount: activeTab.value === 'accepted' ? modalAcceptedPaging.value.totalRecords : modalRejectedPaging.value.totalRecords,
                    providerFn: async () => {
                        // Fetch all creators for this message/tab
                        const paging = activeTab.value === 'accepted' ? modalAcceptedPaging.value : modalRejectedPaging.value
                        const body = {
                            paging: {
                                ...paging,
                                perPage: 1000, // Fetch "all" within limits
                                currentPage: 1
                            }
                        }

                        try {
                            const { data } = await axios.post(`/ai/conversations/${currentConversationId.value}/messages/${message.id}/creators`, body)
                            return transformCreatorsForTable(data.payload.records)
                        } catch (err) {
                            console.error('Failed to fetch creators for export:', err)
                            return []
                        }
                    },
                },
            })
        }

        // Load conversations on mount
        onMounted(async () => {
            await loadConversations()

            // If there's a conversation ID in the URL, load lists first so
            // attachedLists can be reconstructed, then load the conversation
            const conversationId = route.params.conversationId
            if (conversationId) {
                await loadUserLists()
                await loadConversation(conversationId)
                selectedLists.value = selectedListsByConversationId.value[conversationId] || []
            } else {
                loadUserLists() // non-blocking on welcome screen
            }

            // Set up scroll listener for auto-scroll behavior and focus input
            nextTick(() => {
                if (messagesContainer.value) {
                    messagesContainer.value.addEventListener('scroll', handleScroll, { passive: true })
                }

                // Auto-focus appropriate input box
                const inputElement = messages.value.length > 0 ? autoGrowConversation.value : autoGrowWelcome.value
                if (inputElement) {
                    inputElement.focus()
                }
            })
        })

        // Copy conversation to clipboard for debugging
        const copyConversationToClipboard = async () => {
            if (copyButtonState.value === 'copying') return

            copyButtonState.value = 'copying'

            try {
                let markdown = '# Conversation Debug Export\n\n'
                markdown += `**Exported:** ${new Date().toISOString()}\n\n`

                if (messages.value.length === 0) {
                    markdown += '*No messages in conversation*\n'
                } else {
                    markdown += '## Conversation Messages\n\n'

                    messages.value.forEach((message, index) => {
                        if (!message || !message.id) return

                        const role = message.role === 'user' ? '**User**' : '**Assistant**'
                        markdown += `### ${role} (Message ${index + 1})\n\n`

                        if (message.content) {
                            markdown += `${message.content}\n\n`
                        }

                        // Add creator results if present (accepted or rejected)
                        const hasAcceptedCreators = message.creators && message.creators.length > 0
                        const hasRejectedCreators = message.rejectedCreators && message.rejectedCreators.length > 0

                        if (hasAcceptedCreators || hasRejectedCreators) {
                            if (hasAcceptedCreators) {
                                markdown += `#### Creator Results (${message.creators.length} creators)\n\n`
                            } else {
                                markdown += `#### Creator Results (${message.rejectedCreators.length} rejected creators)\n\n`
                            }

                            // Add search methodology if available
                            console.log('🔧 Debug copy - message.toolResults:', message.toolResults)
                            if (message.toolResults && message.toolResults.length > 0) {
                                markdown += '**Search Methodology:**\n\n'
                                message.toolResults.forEach((toolResult, toolResultIndex) => {
                                    try {
                                        const result = JSON.parse(toolResult.output)
                                        if (result.search_details) {
                                            const details = result.search_details
                                            markdown += `\n**Tool Call ${toolResultIndex + 1}: ${details.tool_name || 'Unknown Tool'}**\n\n`

                                            // Input Parameters
                                            if (details.input_parameters) {
                                                markdown += '*Input Parameters:*\n'
                                                Object.entries(details.input_parameters).forEach(([key, value]) => {
                                                    if (value !== null && value !== undefined && value !== '') {
                                                        if (typeof value === 'object') {
                                                            markdown += `- ${key}: ${JSON.stringify(value)}\n`
                                                        } else {
                                                            markdown += `- ${key}: ${value}\n`
                                                        }
                                                    }
                                                })
                                                markdown += '\n'
                                            }

                                            // Processing Steps
                                            if (details.processing_steps) {
                                                markdown += '*Processing Steps:*\n'
                                                details.processing_steps.forEach((step, stepIndex) => {
                                                    markdown += `${stepIndex + 1}. **${step.step.replace(/_/g, ' ').toUpperCase()}**\n`
                                                    Object.entries(step.details).forEach(([key, value]) => {
                                                        if (value !== null && value !== undefined) {
                                                            if (key === 'actual_mongodb_pipeline' && value) {
                                                                markdown += `   - ${key}: [Complex pipeline with ${value.length} stages]\n`
                                                            } else if (typeof value === 'object') {
                                                                markdown += `   - ${key}: ${JSON.stringify(value)}\n`
                                                            } else {
                                                                markdown += `   - ${key}: ${value}\n`
                                                            }
                                                        }
                                                    })
                                                })
                                                markdown += '\n'
                                            }

                                            // Results Summary
                                            if (details.results_summary) {
                                                markdown += '*Results Summary:*\n'
                                                Object.entries(details.results_summary).forEach(([key, value]) => {
                                                    markdown += `- ${key}: ${value}\n`
                                                })
                                                markdown += '\n'
                                            }
                                        }
                                    } catch (e) {
                                        console.error('Error parsing tool result:', e)
                                        markdown += `- **Tool ${index + 1}**: Error parsing tool data\n`
                                    }
                                })
                                markdown += '\n'
                            }

                            markdown += '| ID | Name | Affinity Score | Reasoning |\n'
                            markdown += '|---|---|---|---|\n'

                            // Use accepted creators if available, otherwise use rejected creators
                            const creatorsToShow = hasAcceptedCreators ? message.creators : message.rejectedCreators
                            creatorsToShow.forEach((creator) => {
                                const id = creator._id || 'N/A'
                                const name = creator.display_name || creator.name || 'Unknown'
                                const score = getRelevanceScore(creator) || 'N/A'

                                // Build comprehensive reasoning with evidence
                                const reasoningData = getReasoningData(creator)
                                let reasoning = reasoningData?.recommendation || 'No reasoning available'
                                if (reasoningData?.match_reasons && reasoningData.match_reasons.length > 0) {
                                    reasoning = reasoningData.match_reasons.join(', ')
                                }

                                // Add content evidence if available
                                if (reasoningData?.content_evidence && reasoningData.content_evidence.length > 0) {
                                    reasoning += ` | Evidence: ${reasoningData.content_evidence.join('; ')}`
                                }

                                // Escape pipe characters in content
                                const escapedName = name.replace(/\|/g, '\\|')
                                const escapedReasoning = reasoning.replace(/\|/g, '\\|').replace(/\n/g, ' ')

                                markdown += `| ${id} | ${escapedName} | ${score} | ${escapedReasoning} |\n`
                            })
                            markdown += '\n'
                        }

                        if (message.type === 'error') {
                            markdown += `**Error Type:** ${message.errorType || 'Unknown'}\n\n`
                        }

                        markdown += '---\n\n'
                    })
                }

                // Copy to clipboard
                await navigator.clipboard.writeText(markdown)

                // Show success state
                copyButtonState.value = 'success'

                // Reset to idle after 2 seconds
                setTimeout(() => {
                    copyButtonState.value = 'idle'
                }, 2000)
            } catch (error) {
                console.error('Failed to copy to clipboard:', error)

                // Show error state
                copyButtonState.value = 'error'

                // Reset to idle after 3 seconds
                setTimeout(() => {
                    copyButtonState.value = 'idle'
                }, 3000)
            }
        }

        // Format step timing for display
        const formatStepTiming = (step, index, allSteps) => {
            if (!step.timestamp) return '—'

            // For first step, show time from start
            if (index === 0) {
                return '0.1s'
            }

            // Calculate time difference from previous step
            const currentTime = new Date(step.timestamp)
            const previousTime = new Date(allSteps[index - 1].timestamp)
            const diffMs = currentTime.getTime() - previousTime.getTime()
            const diffSeconds = diffMs / 1000

            if (diffSeconds < 1) {
                return `${Math.max(0.1, diffSeconds).toFixed(1)}s`
            }
            return `${diffSeconds.toFixed(1)}s`
        }

        const maybeSetConversationId = async (incomingConversationId, conversationKey) => {
            if (!incomingConversationId) return
            if (currentConversationId.value === incomingConversationId) return

            const tempId = window.tempConversationId || conversationKey
            currentConversationId.value = incomingConversationId

            // If you're using temp conversations, migrate ASAP (same logic you already have in result)
            const tempConversationIndex = conversations.value.findIndex(
                (c) => c._id === tempId && c.isTemporary,
            )

            if (tempConversationIndex !== -1) {
                const tempConversation = conversations.value[tempConversationIndex]
                conversations.value[tempConversationIndex] = {
                ...tempConversation,
                _id: incomingConversationId,
                isTemporary: false,
                updated_at: new Date().toISOString(),
                last_message_at: new Date().toISOString(),
                }
                migrateConversationMessages(tempId, incomingConversationId)
                if (window.tempConversationId) delete window.tempConversationId
            }

            // Optional: update route right away so future submits use the correct conversationId param
            const routeName = route?.name?.toString() || ''
            if (routeName.startsWith('ai-chat')) {
                router.replace({ name: 'ai-chat-conversation', params: { conversationId: incomingConversationId } })
            }
        }


        // Cleanup intervals on unmount
        onUnmounted(() => {
            Object.values(retryCountdowns.value).forEach((intervalId) => {
                clearInterval(intervalId)
            })
            // Clean up timer interval
            stopTimer()

            // Clean up scroll listener
            if (messagesContainer.value) {
                messagesContainer.value.removeEventListener('scroll', handleScroll)
            }
        })

        // Watch for message changes to auto-scroll
        watch(
            messages,
            (newMessages, oldMessages) => {
                if (newMessages.length > oldMessages.length) {
                    // New message(s) added - scroll to bottom if user hasn't manually scrolled up
                    nextTick(() => {
                        if (!isUserScrolledUp.value) {
                            scrollToBottom()
                        }
                    })
                }
            },
            { deep: true },
        )

        // Watch for conversation changes to scroll to bottom
        watch(currentConversationId, () => {
            // Reset scroll state when switching conversations
            isUserScrolledUp.value = false
            nextTick(() => {
                scrollToBottom(false) // Immediate scroll, no smooth animation
            })
        })

        // Watch for thinking steps changes to auto-scroll during loading
        watch(
            currentThinkingSteps,
            () => {
                // Auto-scroll as thinking steps are added, unless user has scrolled up
                nextTick(() => {
                    if (!isUserScrolledUp.value) {
                        scrollToBottom()
                    }
                })
            },
            { deep: true },
        )

        // Clear list search text when dropdowns close
        watch(isWelcomeListDropdownOpen, (open) => {
            if (!open) listSearchText.value = ''
        })
        watch(isConversationListDropdownOpen, (open) => {
            if (!open) listSearchText.value = ''
        })

        // Persist selected lists per conversation so they survive navigation
        watch(selectedLists, (newVal) => {
            const key = currentConversationId.value || 'new'
            selectedListsByConversationId.value[key] = [...newVal]
        }, { deep: true })

        // Watch for streaming/loading state to scroll when it starts
        watch(
            [isLoading, isStreaming],
            ([loading, streaming]) => {
                if (loading || streaming) {
                    // Scroll to show the loading indicator when it first appears
                    nextTick(() => {
                        if (!isUserScrolledUp.value) {
                            scrollToBottom()
                        }
                    })
                }
            },
        )


        return {
            firstName,
            store,
            availableColumnGroups,
            onEnabledColumnChange,
            enabledColumns,
            paging,
            messages,
            messagesContainer,
            textareaParent,
            autoGrowWelcome,
            autoGrowConversation,
            input,
            isLoading,
            error,
            requestTimer,
            suggestedQueries,
            retryingMessage,
            handleSubmit,
            handleSuggestedQuery,
            handleKeyPress,
            onExportCreators,
            tableColumns,
            transformCreatorsForTable,
            sortedCreators,
            sortedRejectedCreators,
            getRelevanceScore,
            getReasoningData,
            currentTabCreators,
            currentTabRawCreators,
            onFilterChange,
            onPagingChange,
            activeTab,
            resultsView,
            handleFilterChange,
            getAffinityData,
            getErrorIcon,
            getErrorTitle,
            retryLastRequest,
            dismissError,
            formatRetryCountdown,
            getRetryProgress,
            isCreatorsModalOpen,
            isCreatorsModalLoading,
            selectedMessageForModal,
            isCreatorsLoading,
            openCreatorsModal,
            selectedAcceptedCreators,
            selectedRejectedCreators,
            modalAcceptedPaging,
            modalRejectedPaging,
            currentTabPaging,
            getPrimaryPlatform,
            // Conversation management
            conversations,
            currentConversationId,
            isLoadingConversations,
            isLoadingPreviousConversation,
            isLoadingMoreConversations,
            hasMoreConversations,
            handleSelectConversation,
            handleNewConversation,
            handleRenameConversation,
            handleDeleteConversation,
            handleLoadMoreConversations,
            // Creator management
            handleAddToList,
            handleAddToCampaign,
            // Debug utilities
            copyConversationToClipboard,
            copyButtonState,
            // Streaming functionality
            currentThinkingSteps,
            isStreaming,
            currentStreamController,
            formatStepTiming,
            // Thinking steps collapse functionality
            isCurrentThinkingExpanded,
            collapsedThinkingSteps,
            toggleCurrentThinking,
            toggleThinkingSteps,
            // Markdown rendering
            renderMarkdown,
            renderMessageContent,
            handleCreatorLinkClick,
            isThinkingExpanded,
            // Auto-scroll functionality
            scrollToBottom,
            isUserScrolledUp,
            handleScroll,
            // Message interaction functionality
            editingMessageId,
            editingContent,
            copyMessageToClipboard,
            retryFromMessage,
            startEditingMessage,
            cancelEditingMessage,
            saveEditedMessage,
            giveFeedback,

            isDevModeEnabled,

            // Lists functionality
            userLists,
            isLoadingLists,
            isWelcomeListDropdownOpen,
            isConversationListDropdownOpen,
            selectedLists,
            availableLists,
            filteredAvailableLists,
            listSearchText,
            handleListSelect,
            handleListRemove,

            // Creator evaluation
            evaluatingCreators,
            toggleCreatorReasoning,

            // List action
            listActionState,
            listActionEditing,
            listActionIsDestructive,
            listActionConfirmLabel,
            listActionLoadingText,
            listActionDoneText,
            confirmListAction,
            cancelListAction,
        }
    },

    components: {
        SidebarLayout,
        ConversationHistory,
        CreatorResults,
        Button,
        Badge,
        Alert,
        UsersIcon,
        BotIcon,
        SendIcon,
        Loader2Icon,
        SearchIcon,
        TrendingUpIcon,
        MailIcon,
        ExternalLinkIcon,
        DatabaseIcon,
        FilterIcon,
        StarIcon,
        EyeIcon,
        HeartIcon,
        MessageCircleIcon,
        GlobeIcon,
        MapPinIcon,
        CalendarIcon,
        AwardIcon,
        AlertTriangleIcon,
        ClockIcon,
        WifiOffIcon,
        LockIcon,
        RefreshCwIcon,
        XIcon,
        ServerIcon,
        DiscoverTable,
        DiscoveryPagination,
        AffinityPlate,
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        Tooltip,
        TooltipContent,
        TooltipTrigger,
        TooltipProvider,
        ChevronRightIcon,
        ClipboardIcon,
        CheckIcon,
        PencilIcon,
        TableIcon,
        GridIcon,
        ThumbsUpIcon,
        ThumbsDownIcon,
        FileUp,
        PlusIcon,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuLabel,
        DropdownMenuSeparator,
        DropdownMenuTrigger,
        ColumnDisplayManager,
        DiscoveryProfileUnlock,
        CreatorCardsView,
        SkeletonAIChatMessages,
        SkeletonAIInfluencerResults,
    },
}
</script>

<style scoped>
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fade-in {
    animation: fadeIn 0.3s ease-out;
}
</style>
